// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { ErrorInformationStorage } from "../Commons/ErrorInformationStorage.js";
import { Resources } from "../Commons/properties/Resources.js";
import { Point, Rectangle } from "../Commons/Geometry.js";
import { Cursors } from "../Commons/Cursors.js";
import { HistoryType } from "../Utils/History/HistoryType.ts";
import { TerceraChartCashItemSeriesDataType } from "./Series/TerceraChartCashItemSeriesEnums.js";
import { DataCacheToolFactory } from "../Commons/cache/DataCacheToolFactory.js";
import { Font, SolidBrush } from "../Commons/Graphics.js";
import { ToolsCache } from "../Commons/cache/ToolsCache.js";
import { KeyCode, KeyEventProcessor } from "../Commons/KeyEventProcessor.js";
import { MouseButtons } from "../Controls/UtilsClasses/ControlsUtils.js";
import { contextMenuHandler } from "../Utils/AppHandlers.js";
import { PortfolioChartReturnsRenderer } from "../Portfolio/Chart/PortfolioChartReturnsRenderer.js";
import { TerceraChartIndicatorRenderer } from "./Renderers/Indicators/TerceraChartIndicatorRenderer.ts";
import { TerceraChartIndicatorStorageRenderer } from './Renderers/Indicators/TerceraChartIndicatorStorageRenderer.ts';
import { TerceraChartPriceScaleRenderer, TerceraChartPriceScaleRendererAligment, TerceraChartPriceScaleRendererSettings, TerceraChartPriceScaleRendererWindowType } from './Renderers/Scales/TerceraChartPriceScaleRenderer.ts';
import { TerceraChartTimeScaleRendererSettings } from './Renderers/Scales/TerceraChartTimeScaleRenderer.ts';
import { TerceraChartBordersRenderer } from './Renderers/TerceraChartBordersRenderer.ts';
import { TerceraChartCrossHairRenderer } from './Renderers/TerceraChartCrossHairRenderer.ts';
import { TerceraChartDayHighLowRenderer } from './Renderers/TerceraChartDayHighLowRenderer.ts';
import { TerceraChartHighlightExtendedSessionRenderer } from './Renderers/TerceraChartHighlightExtendedSessionRenderer.ts';
import { TerceraChartInfoWindowRenderer } from "./Renderers/TerceraChartInfoWindowRenderer.js"
import { TerceraChartMainPriceRenderer } from './Renderers/TerceraChartMainPriceRenderer.ts';
import { TerceraChartNewAlertRenderer } from './Renderers/TerceraChartNewAlertRenderer.ts';
import { TerceraChartOverlayStorageRenderer } from './Renderers/TerceraChartOverlayStorageRenderer.ts';
import { TerceraChartPlateRenderer } from './Renderers/TerceraChartPlateRenderer.ts';
import { TerceraChartScalingByRectangleRenderer } from './Renderers/TerceraChartScalingByRectangleRenderer.ts';
import { TerceraChartSpreadRenderer } from './Renderers/TerceraChartSpreadRenderer.ts';
import { TerceraChartTimeToNextBarRenderer } from './Renderers/TerceraChartTimeToNextBarRenderer.ts';
import { TerceraChartVolumeBarsRenderer } from './Renderers/TerceraChartVolumeBarsRenderer.ts';
import { TerceraChartWatermarkRenderer } from './Renderers/TerceraChartWatermarkRenderer.ts';
import { TerceraChartEraserRenderer } from './Renderers/Tools/TerceraChartEraserRenderer.ts';
import { TerceraChartNewToolRenderer } from './Renderers/Tools/TerceraChartNewToolRenderer.ts';
import { TerceraChartSelectionRenderer } from './Renderers/Tools/TerceraChartSelectionRenderer.ts';
import { TerceraChartToolInstanceRenderer } from './Renderers/Tools/TerceraChartToolInstanceRenderer.ts';
import { TerceraChartToolRenderer } from './Renderers/Tools/TerceraChartToolRenderer.ts';
import { TerceraChartTradingToolsRenderer } from './Renderers/Tools/TerceraChartTradingToolsRenderer.ts';
import { TradingCentralLinesRenderer } from './Renderers/TradingCentralLinesRenderer.ts';
import { TerceraChartCashItemSeries } from "./Series/TerceraChartCashItemSeries.js";
import { TerceraChartCashItemSeriesSettings } from "./Series/TerceraChartCashItemSeriesSettings.js";
import { TerceraChartOverlaySeries } from "./Series/TerceraChartOverlaySeries.js";
import { TerceraChartAction, TerceraChartActionEnum, TerceraChartToolbarsEnum, CancelActionContext } from "./TerceraChartAction.js";
import { TerceraChartActionProcessor } from "./TerceraChartActionProcessor.js";
import { ModelDataType, TerceraChartMVCCommand, TerceraChartMVCController, TerceraChartMVCModel } from "./TerceraChartMVC.js";
import { TerceraChartOverlay } from "./TerceraChartOverlay.js";
import { ChartHistoryType } from "./Utils/ChartConstants.js";
import { TerceraChartWindow } from "./TerceraChartWindow.js";
import { NewToolClickStrategy } from "./Tools/NewToolClickStrategy.js";
import { TerceraChartWindowsContainerBase } from "./WindowsContainers/TerceraChartWindowsContainerBase.js";
import { CustomEvent } from "../Utils/CustomEvents.js";
import { ThemeManager } from "../Controls/misc/ThemeManager.js";
import { ReloadHistoryParams } from "../Utils/History/ReloadHistoryParams.ts";
import { Periods } from "../Utils/History/TFInfo.ts";
import { CashItemPointsCoverter } from "./Utils/IPointsCoverter.js";
import { ArrayUtils } from "../Utils/ArrayUtils.js";
import { DynProperty } from "../Commons/DynProperty.js";
import { IndicatorManager } from "../Commons/cache/indicators/IndicatorManager.js"
import { DataCache } from "../Commons/DataCache.js";
import { TerceraChartAutoLoadHistoryManager } from "./TerceraChartAutoLoadHistoryManager.js";
import { CashItem } from "../Commons/cache/History/CashItem.ts";
import { ChartMathCoefficients } from "./Utils/ChartMathCoefficients.js";


//
// Чарт, аналог чарт на клиенте
//
export let TerceraChart = function (ctx, terceraChartPanelContext)
{

    //
    // Properties
    //
    //this.instrument = null;         // Instrument
    //this.TimeFrameInfo = null;      // TFInfo

    this.windowsContainer = null;   // TerceraChartWindowsContainerBase
    this.LoadingNow = false;
    this.mainPriceRenderer = null;                   // TerceraChartMainPriceRenderer
    this.mainWindow = null;         // TerceraChartWindow
    this.volumeBarsRenderer = null; //TerceraChartVolumeBarsRenderer

    this.RefreshIsRequired = new CustomEvent();
    this.onHasCorrectDataChanged = new CustomEvent();

    this.model = new TerceraChartMVCModel(DataCache);
    this.model.ModelItemChanged.Subscribe(this.MVCModelItemChanged, this);

    this.chartController = new TerceraChartMVCController(this);
    this.chartController.SuspendRefreshChart = true; // UnSuspendAfter populate

    this.context = ctx;
    this.NoDataMessage = "No data";
    //
    this.w = 0;
    this.h = 0;

    this.BarsToRigth_Bars = 0;

    this.barsToRight_Percent = TerceraChart.DEFAULT_BARSTORIGHT_PERCENT;

    this.lastMouseMoveArgs = null;
    this.MouseState = null;
    this.lastMouseDownState = null;
    this.lastMouseDownI1 = null;
    this.lastMouseDownMaxY = null;
    this.lastMouseDownMinY = null;

    this.terceraChartPanelContext = terceraChartPanelContext;

    this.cashItemSeriesSettings = new TerceraChartCashItemSeriesSettings(this.model);
    this.priceScaleRendererSettings = this.GetChartPriceScaleRendererSettings();

    this.TerceraChartActionProcessor = new TerceraChartActionProcessor(this);
    this.timeScaleRendererSettings = this.GetChartTimeScaleRendererSettings();

    this.TerceraChartScalingByRectangleRenderer;
    this.TerceraChartTradingToolsRenderer;

    this.SelectionRenderer = null;
    this.EraserRenderer = null;

    this.DayHighLowRenderer = null;
    this.SpreadRenderer = null;
    this.PortfolioChartReturnsRenderer = null;
    this.TerceraChartTimeToNextBarRenderer = null;
    this.TerceraChartCrossHairRenderer = null;

    this.TerceraChartFont = new Font(20, ThemeManager.customFontFamily);
    this.TerceraChartNoDataBrushColor = new SolidBrush(ThemeManager.CurrentTheme.ContentColor);
    this.needRedraw = true;
    this.HoverWindow = null;
    this.NewToolStrategy = new NewToolClickStrategy(this.CreateTool.bind(this), this);
    this.ChartID = 13;

    this.overlayStorageRenderer = null;
    this.Overlays = [];

    this.activeOverlay = null;
    this.FocusedOverlay = null;

    this.ContextMenuItems = null;

    this.layers = {};
    this.layersCashedData = {};
    this.initDrawingsLayers();

    this._forceUpdateInitiators = new Array();

    this.InitMainWindow();
    this.MouseIn = false;
    this.snapToCandle = false;
    ToolsCache.OnChangeTool.Subscribe(this.ProcessChangeTool, this);

    this.PortfolioAssetReturnVisible = false;

    this.MovingAlloved = true;

    this.autoLoadManager = new TerceraChartAutoLoadHistoryManager(this);

    this.loadHistoryAbortController = null;
    this.loadOverlayHistoryAbortController = null;
    this.ChartMathCoefficients = new ChartMathCoefficients();

    this.OnUpdateTooltip = new CustomEvent();
};

TerceraChart.prototype.getType = function () { return 'TerceraChart' };

Object.defineProperty(TerceraChart.prototype, 'MainContextMenu', {
    get: function ()
    {
        return this.TerceraChartActionProcessor.CreateMenu(this.ContextMenuItems);
    }
});

Object.defineProperty(TerceraChart.prototype, 'ShowAllSessions', {
    get: function () { return this.model.GetShowAllSessions(); }
});

// Активный оверлей, если установлен для него рисуется шкала на главном окне
TerceraChart.prototype.ActiveOverlayGet = function () { return this.activeOverlay; }
TerceraChart.prototype.ActiveOverlaySet = function (value)
{
    this.activeOverlay = value;
    if (this.FocusedOverlay) this.FocusedOverlay = value;
}

TerceraChart.prototype.Instrument = function ()
{
    return this.model.GetInstrument();
};

TerceraChart.prototype.Account = function ()
{
    return this.model.GetAccount();
};

TerceraChart.prototype.TimeFrameInfo = function ()
{
    return this.model.GetTimeFrameInfo();
};

// History type
TerceraChart.prototype.ChartHistoryType = function ()
{
    return this.model.GetChartHistoryType();
};

TerceraChart.prototype.MainCashItemSeries = function ()
{
    var mainPriceRenderer = this.mainPriceRenderer;
    return mainPriceRenderer ? mainPriceRenderer.Series : null;
};

//
// Создание контейнеров, окон и рендереров необходимых для торгового чарта
//
TerceraChart.prototype.InitMainWindow = function (e)
{
    var priceScaleRender = this.GetChartPriceScaleRenderer(this, this.priceScaleRendererSettings);
    var leftPriceScaleRender = this.GetChartPriceScaleRenderer(this, this.priceScaleRendererSettings);
    leftPriceScaleRender.Aligment = TerceraChartPriceScaleRendererAligment.Left;

    var newToolRenderer = this.GetChartNewToolRenderer();
    var toolsRenderer = this.GetChartToolRenderer(newToolRenderer);
    this.mainWindow = this.GetChartWindow(priceScaleRender, toolsRenderer, leftPriceScaleRender);
    this.mainWindow.PointsConverter = new CashItemPointsCoverter(this.mainWindow, null);
    priceScaleRender.window = this.mainWindow;
    leftPriceScaleRender.window = this.mainWindow;

    this.mainPriceRenderer = new TerceraChartMainPriceRenderer(this);
    this.mainPriceRenderer.cashSettings = this.cashItemSeriesSettings;

    // there was an overlay in the wrong order
    this.volumeBarsRenderer = new TerceraChartVolumeBarsRenderer(this.cashItemSeriesSettings, this);
    this.mainWindow.Renderers.push(this.volumeBarsRenderer);

    this.windowsContainer = this.GetChartWindowsContainerBase(); //TerceraChartWindowsContainer
    this.windowsContainer.AfterResizeWindows.Subscribe(this.windowsContainer_AfterResizeWindows, this);

    this.mainWindow.IsMainWindow = true;
    this.windowsContainer.Windows.push(this.mainWindow);
    this.overlayStorageRenderer = new TerceraChartOverlayStorageRenderer(this);
    this.mainWindow.Renderers.push(this.overlayStorageRenderer);
    this.mainWindow.IndicatorStorageRenderer = new TerceraChartIndicatorStorageRenderer(this, true)
    this.mainWindow.Renderers.push(this.mainWindow.IndicatorStorageRenderer);
    //mainWindow.Renderers.Add(customDrawingRenderer);//1
    this.mainWindow.Renderers.push(this.mainPriceRenderer);
    this.mainWindow.Renderers.push(newToolRenderer); //помещаю ньютуллзрендерер после майнпрайса, чтобы тулзы поверх баров рисовались
    this.mainWindow.Renderers.push(toolsRenderer); // Один рендерер кладем два раза - до mainPriceRenderer и после. Реализуем IsBackground у тулзы.
    this.mainWindow.Renderers.push(this.mainWindow.IndicatorStorageRenderer); // Один рендерер кладем два раза - до mainPriceRenderer и после. Реализуем OnTop у индикаторов.
    this.TerceraChartScalingByRectangleRenderer = new TerceraChartScalingByRectangleRenderer(this);
    this.mainWindow.Renderers.push(this.TerceraChartScalingByRectangleRenderer);
    this.TerceraChartWatermarkRenderer = new TerceraChartWatermarkRenderer(this)
    this.mainWindow.Renderers.splice(0, 0, this.TerceraChartWatermarkRenderer);

    this.DayHighLowRenderer = new TerceraChartDayHighLowRenderer(this);
    this.mainWindow.Renderers.push(this.DayHighLowRenderer);

    this.TerceraChartTimeToNextBarRenderer = this.GetTerceraChartTimeToNextBarRenderer();
    this.mainWindow.Renderers.push(this.TerceraChartTimeToNextBarRenderer);

    this.SpreadRenderer = new TerceraChartSpreadRenderer(this);
    this.mainWindow.Renderers.push(this.SpreadRenderer);

    this.PortfolioChartReturnsRenderer = new PortfolioChartReturnsRenderer(this);
    this.HighlightExtendedSessionRenderer = this.GetChartHighlightExtendedSessionRenderer(this);

    this.mainWindow.Renderers.splice(0, 0, this.HighlightExtendedSessionRenderer, this.PortfolioChartReturnsRenderer);

    this.TerceraChartTradingToolsRenderer = this.GetChartTradingToolsRenderer(newToolRenderer);
    this.mainWindow.Renderers.push(this.TerceraChartTradingToolsRenderer);
    //mainWindow.Renderers.Add(customDrawingRenderer);//2


    this.mainWindow.WindowsContainer = this.windowsContainer;
    this.mainWindow.ToolsRenderer.SelectedToolsListChanged.Subscribe(this.ToolsRenderer_SelectedToolsListChanged, this);

    ////
    //mainWindow.MainThreadRenderers.Add(new TerceraChartInfoWindowRenderer(mainWindow));
    //TerceraChartLineikaRenderer = new TerceraChartLineikaRenderer();
    //mainWindow.MainThreadRenderers.Add(TerceraChartLineikaRenderer);

    // TODO. Change to main thread renderers.
    this.TerceraChartCrossHairRenderer = this.GetTerceraChartCrossHairRenderer()
    this.mainWindow./*MainThread*/Renderers.push(this.TerceraChartCrossHairRenderer);

    if (DataCache.AlertManager.AlertsVisible)
    {
        this.TerceraChartNewAlertRenderer = new TerceraChartNewAlertRenderer(this)
        this.mainWindow.Renderers.push(this.TerceraChartNewAlertRenderer);
    }
    //mainThreadDrawingRenderers.Add(new TerceraChartCrossHairRenderer(this));

    ////windowsContainer.Windows.Add(new TerceraChartWindow());

    this.SelectionRenderer = new TerceraChartSelectionRenderer(this);
    this.EraserRenderer = new TerceraChartEraserRenderer(this);
    this.mainWindow.Renderers.push(this.SelectionRenderer);
    this.windowsContainer.Renderers.push(this.EraserRenderer);
    //windowsContainer.Renderers.Insert(0, new TerceraChartHighlightSessionRenderer());

    this.TerceraChartInfoWindowRenderer = new TerceraChartInfoWindowRenderer(this.mainWindow)
    this._forceUpdateInitiators.push(this.TerceraChartInfoWindowRenderer);
    // TODO. Change to main thread renderers.
    this.mainWindow./*MainThread*/Renderers.push(this.TerceraChartInfoWindowRenderer);
    this.TerceraChartBordersRenderer = this.GetTerceraChartBordersRenderer();
    this.mainWindow.Renderers.push(this.TerceraChartBordersRenderer);

    this.TradingCentralRenderer = new TradingCentralLinesRenderer(this);    // #97276 for TradingCentral only, Visible = false by default
    this.mainWindow.Renderers.push(this.TradingCentralRenderer);
};


TerceraChart.prototype.GetChartNewToolRenderer = function ()
{
    return new TerceraChartNewToolRenderer(this.NewToolStrategy)
}
TerceraChart.prototype.GetChartToolRenderer = function (newToolRenderer)
{
    return new TerceraChartToolRenderer(this.ChartID, this, newToolRenderer);
}
TerceraChart.prototype.GetChartTradingToolsRenderer = function (newToolRenderer)
{
    return new TerceraChartTradingToolsRenderer(this.terceraChartPanelContext, this, this.mainWindow, newToolRenderer);
}

TerceraChart.prototype.GetTerceraChartTimeToNextBarRenderer = function ()
{
    return new TerceraChartTimeToNextBarRenderer(this);
}

TerceraChart.prototype.GetChartPriceScaleRenderer = function (terceraChart, settings, windowType = TerceraChartPriceScaleRendererWindowType.Main)
{
    return new TerceraChartPriceScaleRenderer(terceraChart, settings, windowType);
}

TerceraChart.prototype.GetChartPriceScaleRendererSettings = function ()
{
    return new TerceraChartPriceScaleRendererSettings()
}

TerceraChart.prototype.GetTerceraChartBordersRenderer = function ()
{
    return new TerceraChartBordersRenderer(this);
}

TerceraChart.prototype.GetTerceraChartCrossHairRenderer = function ()
{
    return new TerceraChartCrossHairRenderer(this);
}

TerceraChart.prototype.GetChartTimeScaleRendererSettings = function ()
{
    return new TerceraChartTimeScaleRendererSettings();
}

TerceraChart.prototype.GetChartWindowsContainerBase = function ()
{
    return new TerceraChartWindowsContainerBase(this);
}

TerceraChart.prototype.GetChartWindow = function (priceScaleRender, toolsRenderer, leftPriceScaleRender)
{
    return new TerceraChartWindow(priceScaleRender, toolsRenderer, leftPriceScaleRender);
}

TerceraChart.prototype.GetCharIndicatorRenderer = function (indicator, terceraChart)
{
    return new TerceraChartIndicatorRenderer(indicator, terceraChart);
}

TerceraChart.prototype.GetCharPlateIndicatorRenderer = function (terceraChart)
{
    return new TerceraChartPlateRenderer(terceraChart);
}

TerceraChart.prototype.GetChartHighlightExtendedSessionRenderer = function (terceraChart)
{
    return new TerceraChartHighlightExtendedSessionRenderer(terceraChart);
}

//#region LoadingData

TerceraChart.prototype.LoadHistoryStart = async function ()
{
    this.LoadingNow = true;


    this.loadHistoryAbortController?.abort();
    this.loadHistoryAbortController = new AbortController();
    const signal = this.loadHistoryAbortController.signal;

    const historyParams = this.GetHistoryParams();
    let ins = this.Instrument();

    this.subscribeTrades()      // #103044

    const newCashItem = await CashItem.CreateWithHistory(ins, historyParams, signal);

    if (signal.aborted || newCashItem == null)
        return;

    this.LoadHistoryFinish(newCashItem);
    this.IsDirty()
}

TerceraChart.prototype.LoadHistoryFinish = function (newCashItem, isAutoLoadHistory = false)
{

    this.ReplaceCashItem(newCashItem);

    if (!isAutoLoadHistory)
        this.ToBegin();


    // Не рисуем пока оверлеи - они не валидны
    for (let i = 0; i < this.Overlays.length; i++)
        this.Overlays[i].mainPriceRenderer.InvalidState = true;
    //
    // Replace indicators
    //
    var allIndicators = this.IndicatorRenderers();
    for (let i = 0; i < allIndicators.length; i++)
        allIndicators[i].InvalidState = true;

    this.ReplaceIndicators(allIndicators);

    // To set correct min/max
    if (!this.mainWindow.AutoScale)
        this.mainWindow.AutoFit();

    this.LoadingNow = false;

    this.UpdateOverlay();

    this.IsDirty();
};

// TODO. Refactor. 
TerceraChart.prototype.subscribeTrades = function ()
{
    let ins = this.Instrument()
    if (!ins) return

    DataCache.FQuoteCache.addListener(ins, this, HistoryType.QUOTE_TRADES)
}

TerceraChart.prototype.unsubscribeTrades = function ()
{
    let ins = this.Instrument()
    if (!ins) return

    DataCache.FQuoteCache.removeListener(ins, this, HistoryType.QUOTE_TRADES)
}

TerceraChart.prototype.unsubscribeCashItemFromInstrument = function ()
{
    let cashItemSeries = this.MainCashItemSeries(),
        cashItem = cashItemSeries ? cashItemSeries.CashItem : null
    if (cashItem)
        cashItem.UnSubscribeCurrentInstrument()
}

TerceraChart.prototype.UpdateOverlay = function ()
{
    try
    {
        // Не готовы к перезагрузке
        if (DataCache == null || !DataCache.Loaded)
            return;

        setTimeout(this.UpdateOverlaysAsync.bind(this), 1);
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
    }
};

/// <summary>
/// Обновить оверлеи чарта - выполняется после того как завершено обновление основного
/// </summary>
TerceraChart.prototype.UpdateOverlaysAsync = function ()
{
    try
    {
        for (var i = 0; i < this.Overlays.length; i++)
        {
            var overlay = this.Overlays[i];
            this.LoadOverlayCashItem(overlay);
        }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
    }
};

// TODO. Refactor.
// Syncs chart's mainPriceRenderer and overlay's mainPriceRenderer data for drawing series. 
// If overlay's data is updated faster than data from chart's mainPriceRenderer, 
// overlay's last bar won't be drawn on chart, since overlay's screen data depends on amount of data from main series.
TerceraChart.prototype.recalculateOverlaysIndices = function (overlays)
{
    try
    {
        let overlays = this.Overlays
        for (let i = 0, len = overlays.length; i < len; i++)
        {
            let overlay = overlays[i]
            overlay.recalculateIndices()
        }
    }
    catch (e) 
    {
        ErrorInformationStorage.GetException(e);
        // console.error(e)
    }
}

// TODO. Refactor.
TerceraChart.prototype.LoadOverlayCashItem = async function (overlay)
{
    let tfInfo = this.TimeFrameInfo()
    tfInfo = tfInfo.Copy({ historyType: overlay.HistoryType })

    // TODO. Non fixed list?
    let ins = DataCache.getInstrumentByName(overlay.Instrument)
    if (!ins)
    {
        console.error('Instrument not found.')
        return
    }

    // UGLY. Duplcate of GetHistoryParams().
    let historyParams = this.GetHistoryParams()
    historyParams.TimeFrameInfo = tfInfo

    historyParams.UseDefaultInstrumentHistoryType =
        overlay.HistoryType === ChartHistoryType.Default

    historyParams.symbol = ins.ShortName
    historyParams.route = ins.Route

    this.loadOverlayHistoryAbortController?.abort();
    this.loadOverlayHistoryAbortController = new AbortController();
    const signal = this.loadOverlayHistoryAbortController.signal;

    const newCashItem = await CashItem.CreateWithHistory(ins, historyParams, signal);
    if (signal.aborted || newCashItem == null) //new load history started
        return;

    this.OnLoadOverlayCashItemFinish(overlay, newCashItem);
}

TerceraChart.prototype.OnLoadOverlayCashItemFinish = function (overlay, newCashItem)
{
    // Создаем серию
    let cashItemSeries = new TerceraChartOverlaySeries(
        newCashItem,
        this.model.HistoryFrom,
        this.model.GetHistoryTo(),
        overlay.cashItemSeriesSettings,
        this.mainPriceRenderer.Series)

    overlay.mainPriceRenderer.Series = cashItemSeries

    if (cashItemSeries)
        cashItemSeries.HistoryExpanded.Subscribe(this.IsDirtyHist, this)

    // To set correct min/max
    if (overlay.window.FminFloatY === 0 && overlay.window.FmaxFloatY === 0)
        overlay.window.AutoFit()

    // Оверлей валиден - можно рисовать
    overlay.mainPriceRenderer.InvalidState = false

    // Need for update OverlayWindow rectangle
    this.LayoutWindowsContainers()
}

//#endregion

//#region Drawing

/// <summary>
///
/// </summary>
TerceraChart.prototype.DrawNoData = function (gr)
{

    var ins = this.Instrument();
    var tinf = this.TimeFrameInfo();
    let insStr = "";
    if (ins)
        insStr = ins.DisplayName();
    if (this.LoadingNow && ins && tinf)
        this.NoDataMessage = Resources.getResource("chart.loadingData") + " " + insStr + " " + Periods.getTicNameShort(tinf.Periods) + " ...      ";
    else if (ins && tinf)
        this.NoDataMessage = Resources.getResource("chart.noAvailableData") + " " + insStr + " " + Periods.getTicNameShort(tinf.Periods) + " ...      ";
    else
        this.NoDataMessage = Resources.getResource("chart.noAvailableData");

    var r = new Rectangle(0, 0, this.w, this.h);//this.ClientRectangle;


    var textSizes = gr.measureText(this.NoDataMessage);
    var locationImg = (r.Width - textSizes.width - 42) / 2;

    if (locationImg > 0)
    {
        var img = ThemeManager.CurrentTheme.messageboxIconInfoImage;
        if (img) gr.drawImage(img, locationImg, r.Height / 2 - 16);

        gr.DrawString(this.NoDataMessage, this.TerceraChartFont, this.TerceraChartNoDataBrushColor, locationImg + 42, r.Height / 2 - this.TerceraChartFont.Height / 2);
    }
    else
    {
        gr.DrawString(this.NoDataMessage, this.TerceraChartFont, this.TerceraChartNoDataBrushColor, r.X, r.Y);
    }
}

//
// Рисование чарта
//
TerceraChart.prototype.Draw = function ()
{
    var mainPriceRenderer = this.mainPriceRenderer;
    if (mainPriceRenderer == null)
        return;

    //
    var mainCashItemSeries = mainPriceRenderer.Series;
    if (mainCashItemSeries != null)
    {
        // Calculate BarstoRight
        this.BarsToRigth_Bars = this.CalculateBarToRigth_Bars(this.getBarsToRight_Percent());

        // Check i1
        if (this.mainWindow.i1 > mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars)
        {
            this.mainWindow.i1 = Math.floor(mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars);
            this.IsDirty();
        }
        if (this.mainWindow.i1 < this.mainWindow.im())
            this.mainWindow.i1 = Math.floor(this.mainWindow.im());

        // Sticking to left bar enabled?
        if (this.mainWindow.StickToEnd)
            this.mainWindow.i1 = Math.floor(mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars);


        // Synhronize i1, XScale between all windows
        for (var i = 1; i < this.windowsContainer.Windows.length; i++)
        {
            this.windowsContainer.Windows[i].i1 = this.mainWindow.i1;
            this.windowsContainer.Windows[i].XScale = this.mainWindow.XScale;
        }


        //
        // +++ Женя: баг с автомасштабом учитывается 1 бар за началом экрана
        //
        var firstBarOnTheScreenIndex = Math.floor(this.mainWindow.i1 - this.mainWindow.im()) + 1;


        // Cache cashitem data to array for quick acces
        mainCashItemSeries.CacheScreenData(firstBarOnTheScreenIndex, this.mainWindow.i1, this.Instrument());

        // Overlays:
        // - new logic: automatically set ActiveOverlay if null
        var Overlays = this.Overlays;
        if (this.ActiveOverlayGet() == null || !this.ActiveOverlayGet().SeparateScaleGet())
        {
            // need reset - search first correct overlay
            var newOverlay = null;
            for (var i = 0; i < Overlays.length; i++)
                if (Overlays[i].SeparateScaleGet())
                {
                    newOverlay = Overlays[i];
                    break;
                }
            this.ActiveOverlaySet(newOverlay);
        }
        // Synronize by active overlays
        if (this.activeOverlay != null)
        {
            for (var i = 0; i < Overlays.length; i++)
            {
                if (Overlays[i] == this.activeOverlay || !Overlays[i].SeparateScaleGet())
                    continue;

                Overlays[i].cashItemSeriesSettings.DataType = this.activeOverlay.cashItemSeriesSettings.DataType;
                Overlays[i].window.AutoScale = this.ActiveOverlayGet().window.AutoScale;
            }
        }

        // - Cache cashitem data for overlay
        // - Set visible to active PriceScaleRenderer
        this.mainWindow.LeftPriceScaleRenderer.Visible = this.ActiveOverlayGet() == null;
        for (var i = 0; i < Overlays.length; i++)
        {
            var overlay = Overlays[i];
            overlay.window.XScale = this.mainWindow.XScale;
            overlay.PriceScaleRenderer.Visible = this.ActiveOverlayGet() == Overlays[i] && this.ActiveOverlayGet().SeparateScaleGet();
            if (overlay.mainPriceRenderer != null && overlay.mainPriceRenderer.Series != null)
                overlay.mainPriceRenderer.Series.CacheScreenData(firstBarOnTheScreenIndex, this.mainWindow.i1, overlay.ActiveInstrument);
        }
    }

    // Clear old image
    this.context.clearRect(0, 0, this.w, this.h);
    this.clearLayers();

    if (!this.HasCorrectData())
    {
        this.DrawNoData(this.context);
        return;
    }

    // Set correct min/max for windows
    this.CalculateMinMax();

    this.DrawRenderersOnLayers();
    this.drawLayersData();
};

TerceraChart.prototype.AddForceUpdate = function ()
{
    for (let i = 0; i < this._forceUpdateInitiators.length; i++)
    {
        let rend = this._forceUpdateInitiators[i];
        if (rend.IsNeedForceUpdate())
            this.IsDirty(rend.GetAssignLayer())
    }

    this.autoLoadManager.update();
}

TerceraChart.prototype.DrawRenderersOnLayers = function ()
{

    var i;
    var len = this.layersCashedData.layersLength;
    // Get all drawing params
    var advParams = this.GetAdvancedParamsForRenderer(this.context);
    var w = this.windowsContainer.Windows;
    var l = w.length;
    for (i = 0; i < l; i++)
    {
        if (w[i].CalcScales())
        {
            this.IsDirty(true);
            this.clearLayers();
        }
    }
    for (i = 0; i < len; i++)
    {
        var key = this.layersCashedData.keys[i];
        var lay = this.layers[key];
        if (!lay.isDirty)
            continue;
        var gr = lay.context;
        advParams.layerId = parseInt(key);
        // Start drawing renderers
        this.windowsContainer.Draw(gr, advParams);
    }
};

//
//
//
TerceraChart.prototype.GetAdvancedParamsForRenderer = function (gr)
{
    var p = {};
    p.Instrument = this.Instrument();
    p.Account = this.Account();
    p.mainPriceRenderer = this.mainPriceRenderer;
    p.Font = this.TerceraChartFont;
    p.CursorPosition = this.CursorPosition;
    p.HoverWindowXValue = this.HoverWindowXValue;
    p.HoverWindowYValue = this.HoverWindowYValue;
    p.TerceraChart = this;
    p.DrawPointers = [];
    p.LayoutInfo = this.GetTerceraChartPriceScaleLayoutInfo(gr);
    p.layers = this.layers;
    p.Tag = {};
    p.layerId = LayersEnum.Main;
    p.exitAfterFirst = null;
    p.isNeedConnectFirstAndLastPoints = true;
    return p;
}

TerceraChart.prototype.GetTerceraChartPriceScaleLayoutInfo = function (gr)
{
    var res = new TerceraChartPriceScaleLayoutInfo();

    //
    // Right scale
    //
    var maxW = 0;
    for (var i = 0; i < this.windowsContainer.Windows.length; i++)
    {
        var curW = this.windowsContainer.Windows[i].PriceScaleRenderer.GetPreferredWidth(this.mainPriceRenderer.Series, gr, this.Instrument());
        if (maxW < curW)
            maxW = curW;

        // Indicator digits
        if (i > 0)
        {
            var priceScaleRend = this.windowsContainer.Windows[i].PriceScaleRenderer;

            var digitsBasedOnIndicator = -1;

            for (var j = 0; j < this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators.length; j++)
            {
                var rend = this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators[j];

                // Get maximum from all indicators for current window
                if (rend != null && rend.PTLIndicator != null && rend.PTLIndicator.Digits != -1 && rend.PTLIndicator.Digits < 8 && rend.PTLIndicator.Digits > digitsBasedOnIndicator)
                    digitsBasedOnIndicator = rend.PTLIndicator.Digits;
            }

            priceScaleRend.DigitsBasedOnIndicator = digitsBasedOnIndicator;
        }
    }

    // Celing to 5 pixels
    var ceilingVal = Math.floor(Math.ceil(maxW / 5.0) * 5);
    res.PreferredWidthScales = ceilingVal > TerceraChartPriceScaleRenderer.MIN_WIDTH ? ceilingVal : TerceraChartPriceScaleRenderer.MIN_WIDTH;
    //
    // Left scale
    //

    // 1. Наличие оверлея включает левую шкалу для всего чарта
    if (this.Overlays.length > 0)
    {
        maxW = 0;
        for (var i = 0; i < this.Overlays.length; i++)
        {
            // Not loaded yet
            if (this.Overlays[i].mainPriceRenderer == null || this.Overlays[i].mainPriceRenderer.Series == null || !this.Overlays[i].separateScale)
                continue;

            var curW = this.Overlays[i].window.LeftPriceScaleRenderer.GetPreferredWidth(this.Overlays[i].mainPriceRenderer.Series, gr, this.Overlays[i].ActiveInstrument);
            if (maxW < curW)
                maxW = curW;
        }

        // Celing to 5 pixels
        res.PreferredWidthScalesLeft = Math.floor(Math.ceil(maxW / 5.0) * 5);

        //
        res.HasOverlays = res.PreferredWidthScalesLeft > 0;

        // Аналитик попросил одинаковую ширину
        if (res.HasOverlays)
            res.PreferredWidthScalesLeft = res.PreferredWidthScales = Math.max(res.PreferredWidthScales, res.PreferredWidthScalesLeft);
    }
    else
        res.HasOverlays = false;


    return res;
};

//
//
//
TerceraChart.prototype.CalculateBarToRigth_Bars = function (barsPercent)
{
    if (this.mainWindow != null && this.mainWindow.XScale != 0)
        return Math.round(this.mainWindow.ClientRectangle.Width / this.mainWindow.XScale * barsPercent / 100.0);
    else
        return 0;
}

TerceraChart.prototype.getBarsToRight_Percent = function ()
{
    return this.barsToRight_Percent;
}

TerceraChart.prototype.setBarsToRight_Percent = function (val)
{
    this.barsToRight_Percent = val;

    this.BarsToRigth_Bars = this.CalculateBarToRigth_Bars(this.barsToRight_Percent);
}

//
//
//
TerceraChart.prototype.HasCorrectData = function ()
{
    let hasCorrectData = this.mainPriceRenderer !== null &&
        this.mainPriceRenderer.Series !== null &&
        this.mainPriceRenderer.Series.Count() >= 1 &&
        /*loadingThread == null убран в пользу loadingNow*/
        !this.LoadingNow;

    let onHasCorrectDataChanged = this.oldHasCorrectData != hasCorrectData
    if (onHasCorrectDataChanged)
    {
        this.oldHasCorrectData = hasCorrectData
        this.onHasCorrectDataChanged.Raise(hasCorrectData)
    }

    return hasCorrectData;
};

TerceraChart.prototype.initDrawingsLayers = function ()
{
    var i;
    var keys = Object.keys(Object.keys(LayersEnum));
    var len = keys.length;
    var l = {}

    for (i = 0; i < len; i++)
        l[keys[i]] = new Layer(document.createElement('canvas'));

    this.layers = l;
    this.layersCashedData.keys = Object.keys(this.layers);
    this.layersCashedData.layersLength = this.layersCashedData.keys.length;
}

TerceraChart.prototype.drawLayersData = function ()
{
    var i;
    var len = this.layersCashedData.layersLength;
    for (i = 0; i < len; i++)
    {
        var lay = this.layers[this.layersCashedData.keys[i]];
        this.context.drawImage(lay.canvas, 0, 0);
        lay.isDirty = false;
    }
};

TerceraChart.prototype.clearLayers = function ()
{
    var i;
    var len = this.layersCashedData.layersLength;
    for (i = 0; i < len; i++)
    {
        var lay = this.layers[this.layersCashedData.keys[i]];
        if (lay.isDirty)
            lay.context.clearRect(0, 0, this.w, this.h);
    }
}

TerceraChart.prototype.resizeLayers = function (w, h)
{
    var i;
    var len = this.layersCashedData.layersLength;
    for (i = 0; i < len; i++)
    {
        var lay = this.layers[this.layersCashedData.keys[i]];
        lay.canvas.width = w;
        lay.canvas.height = h;
    }
};

TerceraChart.prototype.needRedrawAllLayers = function ()
{
    var i;
    var len = this.layersCashedData.layersLength;
    for (i = 0; i < len; i++)
        var lay = this.layers[this.layersCashedData.keys[i]].isDirty = true;
};
//#endregion


//#region Process mouse events.
TerceraChart.prototype.ProcessMouseMove = function (e)
{
    var mouseMoveProcessed = false;
    this.lastMouseMoveArgs = e;
    //
    // Идёт тягание чарта
    //
    if (this.MouseState == TerceraChartMouseState.Moving && this.MovingAlloved)
    {
        var xDelta = e.Location.X - this.lastMouseDownState.X;
        var barDelta = xDelta / (this.mainWindow.ClientRectangle.Width / this.mainWindow.im());
        this.CheckAndSeti1(Math.round(this.lastMouseDownI1 - barDelta), true);

        //
        var activeWindow = this.GetActiveWindow();
        if (activeWindow != null && !activeWindow.AutoScale)
            this._moveScreenByY(activeWindow, e.Location.Y - this.lastMouseDownState.Y)
        // т.к. не вызывается base.MouseMove
        this.UpdateCursor(e);

        mouseMoveProcessed = true;
        e.NeedRedraw = true;
    }
    else
    {
        try
        {
            mouseMoveProcessed = this.windowsContainer.ProcessMouseMove(e);
        }
        finally
        {
            //
            // Update Cursor/Tooltip
            //
            this.UpdateCursor(e);
            this.UpdateTooltip(e);
        }

        if (!mouseMoveProcessed)
        {
            var i;
            var len = this.mainWindow.Renderers.length;
            let res
            for (i = 0; i < len; i++)
                res = this.mainWindow.Renderers[i].ProcessMouseMove(e);
        }

        //// Synhronize cursor between all charts
        //if (Utils.SynhronizeCursor && MouseIn)
        //{
        //    var priceToSynch = 0;
        //    var dataType = cashItemSeriesSettings != null ? cashItemSeriesSettings.DataType : TerceraChartCashItemSeriesDataType.Absolute;
        //    switch (dataType)
        //    {
        //        case TerceraChartCashItemSeriesDataType.Absolute:
        //            priceToSynch = mainWindow.PointsConverter.GetDataY(CursorPosition.Y);
        //            break;

        //        case TerceraChartCashItemSeriesDataType.Relative:
        //            priceToSynch = cashItemSeriesSettings.relativeDataConverter.Revert(mainWindow.PointsConverter.GetDataY(CursorPosition.Y));
        //            break;
        //        case TerceraChartCashItemSeriesDataType.Log:
        //            priceToSynch = cashItemSeriesSettings.logDataConverter.Revert(mainWindow.PointsConverter.GetDataY(CursorPosition.Y));
        //            break;
        //    }

        //    //Hashtable syncParam = new Hashtable();
        //    //syncParam["time"] = (long)mainWindow.PointsConverter.GetDataX(CursorPosition.X);
        //    //syncParam["price"] = priceToSynch;
        //    //terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.SynhrCursor, syncParam);
        //}
    }

    //
    if (!mouseMoveProcessed)
    {

    }

    if (e.NeedRedraw === true)
        this.IsDirty();
    else if (!e.NeedRedraw)
        ;
    else if (e.NeedRedraw.length)
    {
        var i;
        var len = e.NeedRedraw.length;
        for (i = 0; i < len; i++)
            this.IsDirty(e.NeedRedraw[i]);
    }
    else
        this.IsDirty(e.NeedRedraw)


    return mouseMoveProcessed;
};

TerceraChart.prototype.GetActiveWindow = function ()
{
    return this.FocusedOverlay != null ? this.FocusedOverlay.window : this.HoverWindow;
}

TerceraChart.prototype._moveScreenByY = function (activeWindow, yDelta)
{
    var mouseDownWindow = this.FocusedOverlay != null ? this.FocusedOverlay.window : this.GetWindowByPoint(this.lastMouseDownState);

    // Только если клик был на том же окне (http://tp.pfsoft.net/entity/38276)
    if (mouseDownWindow == activeWindow)
    {
        var priceDelta = yDelta / (activeWindow.ClientRectangle.Height / (activeWindow.FmaxFloatY - activeWindow.FminFloatY));

        // check
        activeWindow.SetMinMaxScale(this.lastMouseDownMaxY + priceDelta, this.lastMouseDownMinY + priceDelta, this.cashItemSeriesSettings.DataType == TerceraChartCashItemSeriesDataType.Log ? this.cashItemSeriesSettings.logDataConverter : null);
    }
}

TerceraChart.prototype.ProcessMouseDown = function (e)
{
    var mouseDownProcessed = false;
    //for (var i = 0; i < this.windowsContainer.length; i++)
    //{
    mouseDownProcessed = this.windowsContainer.ProcessMouseDown(e);
    //if (res)
    //break;
    //}

    this.lastMouseDownState = e.Location;
    //if (MainContextMenu is IStaticContextMenu)
    //((IStaticContextMenu)MainContextMenu).Assign(this);// for support static menu

    // double call renderers -----------------------------------------------------------------------------------------------
    //if (!mouseDownProcessed)
    //{
    //    var i;
    //    var len = this.mainWindow.Renderers.length;;
    //    for (i = 0; i < len; i++)
    //        mouseDownProcessed = this.mainWindow.Renderers[i].ProcessMouseDown(e);
    //}

    // Рендереры не обработали
    if (!mouseDownProcessed)
    {
        //
        // Тягание чарта?
        //
        var activeWindow = this.FocusedOverlay != null ? this.FocusedOverlay.window : (e.window != null ? e.window : this.mainWindow);
        if (e.Button == MouseButtons.Left && activeWindow.ClientRectangle.Contains(e.Location.X, e.Location.Y) && this.mainPriceRenderer != null && this.mainPriceRenderer.Series != null)
        {
            this.lastMouseDownI1 = this.mainWindow.i1; // ! from main
            this.lastMouseDownMaxY = activeWindow.FmaxFloatY;
            this.lastMouseDownMinY = activeWindow.FminFloatY;
            this.MouseState = TerceraChartMouseState.Moving;
            mouseDownProcessed = true;
            this.autoLoadManager.processMouseDown();
        }
    }

    return mouseDownProcessed;
};

TerceraChart.prototype.ProcessMouseUp = function (e)
{
    //from base
    try
    {
        //
        // Cancel action on RightButton
        //
        if (e.Button === MouseButtons.Right && this.CancelAction(CancelActionContext.RightButton))
            return;

        var mouseProcessed = false;

        mouseProcessed = this.windowsContainer.ProcessMouseUp(e);
        if (mouseProcessed)
        {
            this.UpdateCursor(e);
            this.UpdateTooltip(e);
        }

        // double call renderers -----------------------------------------------------------------------------------------------
        //if (!mouseProcessed)
        //{
        //    var i;
        //    var len = this.mainWindow.Renderers.length;
        //    for (i = 0; i < len; i++)
        //        mouseProcessed = this.mainWindow.Renderers[i].ProcessMouseUp(e);
        //}

        this.MouseState = TerceraChartMouseState.None;
        this.IsDirty();

        if (!mouseProcessed)
            if (e.Button === MouseButtons.Right)
            {
                var contextMenuItems = this.GetContextMenu(e, this);
                if (contextMenuItems !== null)
                    contextMenuHandler.Show(contextMenuItems, e.ClientLocation.X, e.ClientLocation.Y);
            }
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        console.log(ex);
    }
    return mouseProcessed
};

TerceraChart.prototype.SetMouseStateNone = function ()
{
    this.MouseState = TerceraChartMouseState.None;
}

TerceraChart.prototype.ProcessMouseLeave = function (e)
{
    try
    {
        //for (var i = 0; i < this.windowsContainer.Count; i++)
        this.windowsContainer.ProcessMouseLeave(e);

        this.OnMouseLeaveActions();

        //
        this.IsDirty();
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        console.log(ex);
    }
    this.UpdateCursor(null);
    this.UpdateTooltip(null);
};

TerceraChart.prototype.OnMouseLeaveActions = function ()
{
    //
    this.HoverWindow = null;
    this.CursorPosition = Point.Empty();
    this.HoverWindowYValue = 0;
    this.MouseIn = false;
}

TerceraChart.prototype.ProcessMouseEnter = function (e)
{
    try
    {
        //for (var i = 0; i < this.windowsContainer.Count; i++)
        this.windowsContainer.ProcessMouseEnter(e);

        this.MouseIn = true;
        this.IsDirty();
    }
    catch (ex)
    {
        ErrorInformationStorage.GetException(ex);
        console.log(ex);
    }

};

TerceraChart.prototype.ProcessMouseDoubleClick = function (e)
{
    var res = false;
    //for (var i = 0; i < this.windowsContainer.Count; i++)
    //{
    res = this.windowsContainer.ProcessMouseDoubleClick(e);
    //    if (res)
    //        return res;
    //}
    return res;
};

TerceraChart.prototype.ProcessMouseClick = function (e)
{
    var res = false;
    //for (var i = 0; i < this.windowsContainer.Count; i++)
    //{
    res = this.windowsContainer.ProcessMouseClick(e);
    //    if (res)
    //        return res;
    //}

    if (!res)
    {
        res = this.autoLoadManager.processMouseClick();
    }

    return res;
};


TerceraChart.prototype.UpdateNewOrderTool = function (data)
{
    let TerceraChartNewToolRenderer = this.mainWindow.GetMainThreadRenderer("TerceraChartTradingToolsRenderer");

    if (TerceraChartNewToolRenderer)
        TerceraChartNewToolRenderer.UpdateNewOrderTool(data)
}


TerceraChart.prototype.ProcessMouseWheel = function (e)
{
    var res = false;
    //for (var i = 0; i < this.windowsContainer.Count; i++)
    //{
    res = this.windowsContainer.ProcessMouseWheel(e);
    //    if (res)
    //        return res;
    //}
    if (res)
        this.IsDirty();
    return res;
};

TerceraChart.prototype.ImproveArgs = function (ev)
{
    var X = ev.offsetX;
    var Y = ev.offsetY;

    var HoverWindow = this.SetChartCursorPosition(X, Y);

    // Стараемся не перерисовывать чарт на каждое движение мыши
    var ev = new TerceraChartMouseEventArgs(HoverWindow, ev);
    return ev;
};

TerceraChart.prototype.SetChartCursorPosition = function (X, Y)
{
    this.CursorPosition = new Point(X, Y);
    var HoverWindow = this.GetHoverWindow(new Point(X, Y));
    if (HoverWindow != null)
    {
        this.HoverWindowXValue = HoverWindow.PointsConverter.GetDataX(X);
        this.HoverWindowYValue = HoverWindow.PointsConverter.GetDataY(Y);
    }
    else
    {
        this.HoverWindowYValue = 0;
        this.HoverWindowXValue = 0;
    }

    this.HoverWindow = HoverWindow;

    return HoverWindow;
}

/// <summary>
/// Определение активного окна
/// </summary>
TerceraChart.prototype.GetHoverWindow = function (pt)
{
    //for (var i = 0; i < this.windowsContainer.Count; i++)
    //{
    for (var j = 0; j < this.windowsContainer.Windows.length; j++)
    {
        if (this.windowsContainer.Windows[j].Rectangle.Contains(pt.X, pt.Y))
            return this.windowsContainer.Windows[j];
    }
    //}
    return null;
}

TerceraChart.prototype.GetWindowByPoint = function (pt)
{
    if (this.windowsContainer == null)
        return null;

    //foreach (var wCont in WindowsContainers)
    //{
    if (this.windowsContainer.Windows != null)
    {
        for (var j = 0; j < this.windowsContainer.Windows.length; j++)
        {
            var clientRect = this.windowsContainer.Windows[j].ClientRectangle;
            if (clientRect.Contains(pt.X, pt.Y))
            {
                return this.windowsContainer.Windows[j];
            }
        }
        //}
    }

    return null;
}

//#endregion

TerceraChart.prototype.OnKeyDown = function ()
{
    var keyProc = KeyEventProcessor;
    var currentButton = keyProc.currentButton;

    if (keyProc.isCtrlModOnly() && currentButton === KeyCode.A)
    {
        this.mainWindow.ToolsRenderer.SelectAllTools();
        this.IsDirty(LayersEnum.Tools);
        return;
    }

    if (keyProc.isCtrlModOnly() && currentButton === KeyCode.L)
    {
        if (Resources.isHidden('property.hotkeyManager.ShowVolume'))
            return;
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.View, TerceraChartToolbarsEnum.Volume));
        this.IsDirty();
        return;
    }


    switch (currentButton)
    {
        case KeyCode.ESC:
            this.CancelAction(CancelActionContext.Esc);
            break;
        case KeyCode.DELETE:
            this.RemoveSelectedAndHoveredToolsFromAllWindows();
            this.IsDirty(LayersEnum.Tools);
            break;
    }
};

TerceraChart.prototype.RemoveSelectedAndHoveredToolsFromAllWindows = function ()
{
    var windows = this.windowsContainer.Windows;
    var len = windows.length;
    for (var i = 0; i < len; i++)
    {
        var toolsRenderer = windows[i].ToolsRenderer;
        if (toolsRenderer) toolsRenderer.RemoveSelectedAndHoverTools();
    }
};

TerceraChart.prototype.MVCModelItemChanged = function (item)
{
    var model = this.model;
    var windowsContainer = this.windowsContainer;

    switch (item.ItemType)
    {
        case ModelDataType.Style:
            this.mainPriceRenderer.ChartDrawingType = model.GetChartDrawingType();
            break;

        case ModelDataType.Instrument:
            // Inform Renderers about changing instrument
            var newInstrument = model.GetInstrument();
            if (windowsContainer)
            {
                for (var i = 0; i < windowsContainer.Windows.length; i++)
                {
                    var allRenderers = windowsContainer.Windows[i].GetAllRenderers();
                    for (var j = 0; j < allRenderers.length; j++)
                    {
                        var renderer = allRenderers[j];
                        if (renderer.Instrument !== undefined)
                            renderer.Instrument = newInstrument;
                    }
                }
            }
            this.NeedSetDafaultsDataRage = true;
            break;

        case ModelDataType.Account:
            //
            // Inform Renderers about changing account
            //
            var newAcc = model.GetAccount();
            if (windowsContainer)
            {
                for (var i = 0; i < windowsContainer.Windows.length; i++)
                {
                    var allRenderers = windowsContainer.Windows[i].GetAllRenderers();
                    for (var j = 0; j < allRenderers.length; j++)
                    {
                        var renderer = allRenderers[j];
                        if (renderer.Account !== undefined)
                            renderer.Account = newAcc;
                    }
                }
            }
            break;
    }
};

TerceraChart.prototype.IsAtTheBegin = function ()
{
    if (this.MainCashItemSeries() == null)
        return true;

    return this.mainWindow.i1 == this.MainCashItemSeries().Count() - 1 + this.CalculateBarToRigth_Bars(TerceraChart.DEFAULT_BARSTORIGHT_PERCENT) && this.barsToRight_Percent === TerceraChart.DEFAULT_BARSTORIGHT_PERCENT;
}

//
//
//
TerceraChart.prototype.CalculateMinMax = function (e)
{

    this.windowsContainer.CalculateMinMax();

    if (this.overlayStorageRenderer != null)
        this.overlayStorageRenderer.CalculateMinMax();
};

TerceraChart.prototype.ReplaceCashItem = function (newCashItem)
{
    if (this.model == null)
        return;

    var oldSeries = this.mainPriceRenderer != null ? this.mainPriceRenderer.Series : null;

    if (oldSeries !== null)
        oldSeries.HistoryExpanded.UnSubscribe(this.onMainSeriesHistoryExpanded, this);
    var cashItemSeries = new TerceraChartCashItemSeries(newCashItem, this.model.HistoryFrom, this.model.GetHistoryTo(), this.cashItemSeriesSettings);
    this.mainPriceRenderer.Series = cashItemSeries;
    this.windowsContainer.scrollerRenderer.Series = cashItemSeries;
    this.windowsContainer.timeScaleRenderer.Series = cashItemSeries;

    if (cashItemSeries !== null)
        cashItemSeries.HistoryExpanded.Subscribe(this.onMainSeriesHistoryExpanded, this);

    var i;
    var len = this.windowsContainer.Windows.length;

    for (i = 0; i < len; i++)
    {
        var window = this.windowsContainer.Windows[i];
        window.PointsConverter = new CashItemPointsCoverter(window, cashItemSeries);
    }

    if (oldSeries)
    {
        var allIndicators = this.IndicatorRenderers();
        for (var i = 0; i < allIndicators.length; i++)
            oldSeries._cashItem.RemoveIndicator(allIndicators[i].PTLIndicator);
    }


    //List<TerceraChartBaseIndicatorRenderer> allInds = IndicatorRenderers;
    //foreach (TerceraChartBaseIndicatorRenderer rend in allInds)
    //            rend.Series = cashItemSeries;

    // MB TODO fix refresh
    // https://tp.traderevolution.com/entity/102886
    // https://tp.traderevolution.com/entity/103018
    if (oldSeries != null && oldSeries._cashItem !== newCashItem)
        oldSeries.Dispose();

    this.IsDirty();
};

TerceraChart.prototype.onMainSeriesHistoryExpanded = function (hasNewBars)
{
    // TODO. Refactor.
    this.recalculateOverlaysIndices()
    this.IsDirtyHist(hasNewBars)
}

TerceraChart.prototype.ToBegin = function ()
{
    var mainCashItemSeries = this.mainPriceRenderer.Series;
    if (mainCashItemSeries === null)
        return;

    // reset BarsToRight
    this.barsToRight_Percent = TerceraChart.DEFAULT_BARSTORIGHT_PERCENT;

    this.CheckAndSeti1(mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars);
};

TerceraChart.prototype.OnResize = function (w, h)
{

    this.w = w;
    this.h = h;

    this.resizeLayers(w, h);

    this.windowsContainer.Rectangle.Width = w;
    this.windowsContainer.Rectangle.Height = h;
    this.windowsContainer.LayoutWindows();

    this.IsDirty();
};

TerceraChart.prototype.IsDirtyHist = function (hasNewBars)
{
    if (hasNewBars)
        this.IsDirty(true);
    else
    {
        this.IsDirty(LayersEnum.Quotes);
        this.IsDirty(LayersEnum.CrossHair);
    }
}

TerceraChart.prototype.IsDirty = function (result = true)
{
    if (result === true)
        this.needRedrawAllLayers();
    else
        this.layers[result].isDirty = true;

    this.needRedraw = true;
}

/// <summary>
/// Установка привязанному окну параметра I1 с предварительной проверкой.
/// </summary>
TerceraChart.prototype.CheckAndSeti1 = function (newI1, allowChangeBarsToRight = false)
{
    var mainCashItemSeries = this.mainPriceRenderer.Series;
    if (mainCashItemSeries === null)
        return false;

    var mainWindow = this.mainWindow;

    if (newI1 < Math.floor(mainWindow.im()))
    {
        this.autoLoadManager.processDragLeft(newI1);
    }

    //
    // Пытаемся установить правую границу в будущее - разрешаем, только если allowChangeBarsToRight
    //
    if (newI1 > mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars && allowChangeBarsToRight)
    {
        // Calculate BarToRigthPercent
        var newBarsToRigthPercent = (newI1 - (mainCashItemSeries.Count() - 1)) / mainWindow.im() * 100.0;
        if (newBarsToRigthPercent > 90)
        {
            // Limit newBarsToRigthPercent to 90%
            this.setBarsToRight_Percent(90);
            newI1 = mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars;
        }
        else
        {
            this.setBarsToRight_Percent(newBarsToRigthPercent);
        }
    }
    else
    {
        if (newI1 > mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars)
            newI1 = mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars;
    }

    if (newI1 < mainWindow.im() && (mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars) > mainWindow.im())
        newI1 = Math.floor(mainWindow.im());
    // Total history count less then im. i1 should be sticked to right side
    if (mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars < mainWindow.im())
        newI1 = mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars;

    if (mainWindow.i1 != newI1)
    {
        mainWindow.StickToEnd = newI1 == mainCashItemSeries.Count() - 1 + this.BarsToRigth_Bars;
        mainWindow.i1 = Math.floor(newI1);
        return true;
    }
    else
        return false;
};

/// <summary>
///
/// </summary>
TerceraChart.prototype.ZoomToRectangle = function (r, startPoint)
{
    var mainWindow = this.mainWindow;
    if (r.Width < mainWindow.XScale)
        return;

    var i1Time = mainWindow.PointsConverter.GetDataX(r.X + r.Width);
    var newI1Index = this.mainPriceRenderer.Series.FindIntervalExactly(i1Time) - 1;

    var barsInRect = r.Width / mainWindow.XScale;
    var newxScale = mainWindow.ClientRectangle.Width / barsInRect;
    mainWindow.CheckAndSetXScale(Math.round(newxScale));

    this.CheckAndSeti1(Math.round(newI1Index));

    var clickedWindow = this.GetWindowByPoint(startPoint);
    if (clickedWindow)
    {
        var clickedWindowPC = clickedWindow.PointsConverter;

        var RY = r.Y;
        var RB = RY + r.Height;

        var windows = this.windowsContainer.Windows;
        var w_len = windows.length;
        for (var i = 0; i < w_len; i++)
        {
            var ww = windows[i];
            // Анализируем окно, в котором делали зуминг - устаналвиваем значения min/max
            if (ww === clickedWindow)
            {
                clickedWindow.AutoScale = false;
                clickedWindow.FmaxFloatY = clickedWindowPC.GetDataY(RY);
                clickedWindow.FminFloatY = clickedWindowPC.GetDataY(RB);
            }
            // Для остальных окон цену не имеем права ставить, поэтом просто autofit'им
            else
            {
                if (!ww.AutoScale)
                    ww.AutoFit();
            }
        }
    }
    this.Draw();
};

TerceraChart.prototype.GetContextMenu = function (e, chart)
{
    var menuItems = null;

    if (this.HasCorrectData())
    {
        var result = this.windowsContainer.GetContextMenu(e, chart);
        if (result !== null)
            menuItems = result;
    }

  if (menuItems === null || menuItems.length === 0)
        menuItems = this.MainContextMenu;

    //if (menu !== null)
    //    menu.Show();

    return menuItems;
};

TerceraChart.prototype.CancelAction = function (context)
{
    return this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Cancel, context));
}

TerceraChart.prototype.Properties = function ()
{
    var properties = [];

    var i;
    var len = this.windowsContainer.Renderers.length;
    for (i = 0; i < len; i++)
        properties = properties.concat(this.windowsContainer.Renderers[i].Properties());


    var i_w;
    var len_w = this.windowsContainer.Windows.length;
    for (i_w = 0; i_w < len_w; i_w++)
    {
        var window = this.windowsContainer.Windows[i_w];
        var i_r;
        var len_r = window.Renderers.length;
        for (i_r = 0; i_r < len_r; i_r++)
            properties = properties.concat(window.Renderers[i_r].Properties());
    }

    //#region Own Properties

    //
    // Absolute/Relative/Log
    //
    var SeparatorGroup = "#0#" + Resources.getResource("property.SeparatorGroup.ScaleType");
    var prop = null;

    //#region Scale type props. Commented

    /*
    var tfInfo = this.TimeFrameInfo();
    var cashItemSeriesSettings = this.cashItemSeriesSettings;

    if (tfInfo && tfInfo.TradeAnalysisType() !== TradeAnalysisType.Cluster &&
        tfInfo.TradeAnalysisType() !== TradeAnalysisType.MarketProfile)
    {
        prop = new DynProperty("relativeScaleType", cashItemSeriesSettings.DataType, DynProperty.COMBOBOX_COMBOITEM, DynProperty.PRICE_SCALE_GROUP);
        prop.sortIndex = 10;
        prop.objectVariants = [
            { text: Resources.getResource("property.GraphicType.Absolute"), value: TerceraChartCashItemSeriesDataType.Absolute },
            { text: Resources.getResource("property.GraphicType.Relative"), value: TerceraChartCashItemSeriesDataType.Relative },
            { text: Resources.getResource("property.GraphicType.Log"), value: TerceraChartCashItemSeriesDataType.Log }
        ];
        prop.COMBOBOX_TYPE = DynProperty.INTEGER;
        prop.assignedProperty = ["relativeCalcType", "logBase"];
        prop.separatorGroup = SeparatorGroup;

        properties.push(prop);
    }

    prop = new DynProperty("relativeCalcType", cashItemSeriesSettings.BasisType, DynProperty.COMBOBOX_COMBOITEM, DynProperty.PRICE_SCALE_GROUP);
    prop.sortIndex = 20;
    prop.enabilityValue = [TerceraChartCashItemSeriesDataType.Relative, TerceraChartCashItemSeriesDataType.Log];
    prop.enabled =
        cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Relative ||
        cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Log;
    prop.objectVariants = [
        {
            text: Resources.getResource("property.RelativeCalculationType.BeginOfTheData"),
            value: TerceraChartCashItemSeriesDataTypeBasisType.BeginOfData
        },
        {
            text: Resources.getResource("property.RelativeCalculationType.BeginOfTheDay"),
            value: TerceraChartCashItemSeriesDataTypeBasisType.BeginOfDay
        },
        {
            text: Resources.getResource("property.RelativeCalculationType.BeginOfTheScreen"),
            value: TerceraChartCashItemSeriesDataTypeBasisType.BeginOfScreen
        }
    ];
    prop.COMBOBOX_TYPE = DynProperty.INTEGER;
    prop.separatorGroup = SeparatorGroup;

    properties.push(prop);

    prop = new DynProperty("logBase", cashItemSeriesSettings.logDataConverter.LogBase, DynProperty.DOUBLE, DynProperty.PRICE_SCALE_GROUP);
    prop.enabilityValue = [TerceraChartCashItemSeriesDataType.Log];
    prop.enabled = cashItemSeriesSettings.DataType === TerceraChartCashItemSeriesDataType.Log;
    prop.sortIndex = 30;
    prop.minimal = 1;
    prop.maximal = 100;
    prop.increment = 0.01;
    prop.decimalPlaces = 2;
    prop.separatorGroup = SeparatorGroup;

    properties.push(prop);
    */

    //#endregion

    //Scale type
    SeparatorGroup = "#1#" + Resources.getResource("property.SeparatorGroup.Zoom");

    var mainWindow = this.mainWindow;

    prop = new DynProperty("autoYScale", mainWindow.AutoScale, DynProperty.RADIOBUTTON_COMBOITEM, DynProperty.PRICE_SCALE_GROUP);
    prop.assignedProperty = ["FitIndicator", "FitOrders", "FitHighLow", "FitDrawings", "FitAlerts"];
    prop.objectVariants = [
        { text: Resources.getResource("property.autoYScale.Auto"), value: true },
        { text: Resources.getResource("property.autoYScale.Manual"), value: false }
    ];
    prop.COMBOBOX_TYPE = DynProperty.INTEGER;
    prop.separatorGroup = SeparatorGroup;
    prop.sortIndex = 0;
    properties.push(prop);

    // xscale
    prop = new DynProperty("xScale", mainWindow.XScale, DynProperty.DOUBLE, DynProperty.HIDDEN_GROUP);
    properties.push(prop);

    if (this.TerceraChartActionProcessor.GetTerceraChartActionState(
        TerceraChartAction.Create(TerceraChartActionEnum.FitIndicators)).Visible)
    {
        prop = new DynProperty("FitIndicator", mainWindow.indicatorRendererSettings.UseInAutoscale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
        prop.enabilityValue = true;
        prop.enabled = mainWindow.AutoScale;
        prop.sortIndex = 1;
        prop.separatorGroup = SeparatorGroup;

        properties.push(prop);
    }

    if (this.TerceraChartActionProcessor.GetTerceraChartActionState(
        TerceraChartAction.Create(TerceraChartActionEnum.FitOrders)).Visible)
    {
        prop = new DynProperty("FitOrders", this.TerceraChartTradingToolsRenderer.OrderScale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
        prop.enabilityValue = true;
        prop.enabled = mainWindow.AutoScale;
        prop.sortIndex = 2;
        prop.separatorGroup = SeparatorGroup;

        properties.push(prop);
    }

    if (this.TerceraChartActionProcessor.GetTerceraChartActionState(
        TerceraChartAction.Create(TerceraChartActionEnum.FitDayHighLow)).Visible &&
        typeof TerceraChartDayHighLowRenderer === 'function')
    {
        var highLowRenderer = mainWindow.GetRenderer(TerceraChartDayHighLowRenderer);
        if (highLowRenderer)
        {
            prop = new DynProperty("FitHighLow", highLowRenderer.UseInAutoscale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
            prop.enabilityValue = true;
            prop.enabled = mainWindow.AutoScale;
            prop.sortIndex = 3;
            prop.separatorGroup = SeparatorGroup;

            properties.push(prop);
        }
    }

    prop = new DynProperty("FitDrawings", mainWindow.ToolsRenderer.UseInAutoscale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
    prop.enabilityValue = true;
    prop.enabled = mainWindow.AutoScale;
    prop.sortIndex = 4;
    prop.separatorGroup = SeparatorGroup;

    properties.push(prop);

    if (this.TerceraChartActionProcessor.GetTerceraChartActionState(
        TerceraChartAction.Create(TerceraChartActionEnum.FitAlerts)).Visible)
    {
        prop = new DynProperty("FitAlerts", this.TerceraChartTradingToolsRenderer.AlertScale, DynProperty.BOOLEAN, DynProperty.PRICE_SCALE_GROUP);
        prop.enabilityValue = true;
        prop.enabled = mainWindow.AutoScale;
        prop.sortIndex = 5;
        prop.separatorGroup = SeparatorGroup;

        properties.push(prop);
    }

    properties = properties.concat(this.model.Properties());

    //#endregion

    properties = properties.concat(this.priceScaleRendererSettings.Properties());
    properties = properties.concat(this.timeScaleRendererSettings.Properties());

    return properties;
}

TerceraChart.prototype.callBack = function (properties)
{
    var i;
    var len = this.windowsContainer.Renderers.length;
    for (i = 0; i < len; i++)
        this.windowsContainer.Renderers[i].callBack(properties);


    var i_w;
    var len_w = this.windowsContainer.Windows.length;
    for (i_w = 0; i_w < len_w; i_w++)
    {
        var window = this.windowsContainer.Windows[i_w];
        var i_r;
        var len_r = window.Renderers.length;
        for (i_r = 0; i_r < len_r; i_r++)
            window.Renderers[i_r].callBack(properties);
    }

    // TODO.
    //#region Own Properties

    var mainWindow = this.mainWindow;

    var mainSeries = this.MainCashItemSeries();
    var cashItemSeriesSettings = this.cashItemSeriesSettings;

    //#region Scale type props. Commented

    //
    // Absolute/Relative/Log
    //
    /*
    var dp = DynProperty.getPropertyByName(properties, "relativeScaleType");
    if (dp) cashItemSeriesSettings.DataType = dp.value;

    dp = DynProperty.getPropertyByName(properties, "relativeCalcType");
    if (dp) cashItemSeriesSettings.BasisType = dp.value;

    dp = DynProperty.getPropertyByName(properties, "logBase");
    if (dp) cashItemSeriesSettings.logDataConverter.LogBase = dp.value;
    */

    //#endregion

    let dp = DynProperty.getPropertyByName(properties, "autoYScale");
    if (dp)
    {
        this.TerceraChartActionProcessor.ProcessTerceraChartAction(TerceraChartAction.Create(
            dp.value ? TerceraChartActionEnum.AutoScale : TerceraChartActionEnum.ManualScale,
            mainWindow));
    }

    dp = DynProperty.getPropertyByName(properties, "xScale");
    if (dp) mainWindow.XScale = dp.value;

    dp = DynProperty.getPropertyByName(properties, "FitHighLow");
    if (dp)
    {
        var highLowRenderer = mainWindow.GetRenderer(TerceraChartDayHighLowRenderer);
        if (highLowRenderer) highLowRenderer.UseInAutoscale = dp.value;
    }

    dp = DynProperty.getPropertyByName(properties, "FitIndicator");
    if (dp) mainWindow.indicatorRendererSettings.UseInAutoscale = dp.value;

    dp = DynProperty.getPropertyByName(properties, "FitOrders");
    if (dp) this.TerceraChartTradingToolsRenderer.OrderScale = dp.value;

    dp = DynProperty.getPropertyByName(properties, "FitDrawings");
    if (dp) mainWindow.ToolsRenderer.UseInAutoscale = dp.value;

    dp = DynProperty.getPropertyByName(properties, "FitAlerts");
    if (dp) this.TerceraChartTradingToolsRenderer.AlertScale = dp.value;
    //#endregion

    this.priceScaleRendererSettings.callBack(properties);
    this.timeScaleRendererSettings.callBack(properties);

    // в самом конце загрузка пропертей модели и перегруз чарта
    this.model.callBack(properties);

    //todo

    this.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.ShowAllSession), this.ShowAllSessions);
    this.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.ShowEmptyBars), this.cashItemSeriesSettings.ShowEmptyBars);
    this.chartController.ExecuteCommand(new TerceraChartMVCCommand(ModelDataType.HistoryType), this.model.GetChartHistoryType());

    this.windowsContainer.LayoutWindows();
    this.IsDirty();
};

TerceraChart.prototype.RefreshChart = function ()
{
    clearTimeout(this.refreshDelayTimeOut);
    this.refreshDelayTimeOut = null;
    this.refreshDelayTimeOut = setTimeout(this.RefreshChartProcces.bind(this), 300);
    this.autoLoadManager.stopLoading();
    this.autoLoadManager.resetCurrentView();
};

TerceraChart.prototype.RefreshChartProcces = function ()
{
    if (this.Instrument() !== null && this.TimeFrameInfo() !== null)
    {
        this.LoadHistoryStart();
        if (this.TerceraChartTradingToolsRenderer)
            this.TerceraChartTradingToolsRenderer.RepopulateTools();
    }
}

TerceraChart.prototype.CallOnRefreshIsRequired = function ()
{
    this.RefreshIsRequired.Raise();
};

TerceraChart.prototype.AddIndicator = function (indicator, setupProperties = false)
{
    if (indicator === null)
        return false;


    // assign current settings
    indicator.Symbol = this.Instrument().GetInteriorID();

    indicator.TimeFrameInfo = this.TimeFrameInfo();
    indicator.MainChart = this;

    ////set cashItem
    //indicator.setCashItem(this.mainPriceRenderer.Series._cashItem);

    //
    // Edit properties
    //
    let indicatorRenderer = this.GetCharIndicatorRenderer(indicator, this);
    var success = setupProperties ? indicatorRenderer.editProperties(this) : true;
    if (!setupProperties)
        this.AddIndicatorProcess(indicatorRenderer);

    // Корректировка (могли поменять номер окна)
    //this.CorrectIndicatorWindows();
    //if (!success)
    //    return false;
    //    // +++ support default settings
    //    customInd.LoadSchemeMyProperties();
    //
    //
    //
    //Hashtable par = new Hashtable();
    //par["PTLIndicator"] = indicator;
    //par["Offline"] = false;
    //SeparateThread.QueueUserWorkItem(new WaitCallback(AddingIndicatorAsync), par);
    //
    // Определяемся с окном
    //
    //indicatorRenderer.Series = this.mainPriceRenderer.Series;
    //this.mainWindow.IndicatorStorageRenderer.Indicators.push(indicatorRenderer);
    ////
    //this.CorrectIndicatorWindows();
    //return true;
};

TerceraChart.prototype.AddIndicatorProcess = function (indicatorRenderer)
{
    var indicator = indicatorRenderer.Indicator();
    // assign current settings
    indicator.Symbol = this.Instrument().GetInteriorID();

    indicator.TimeFrameInfo = this.TimeFrameInfo();
    indicator.MainChart = this;

    //set cashItem
    let ind = this.mainPriceRenderer.Series._cashItem.AddIndicator(indicator);
    if (ind !== null)
        ind.Refresh();
    indicatorRenderer.Series = this.mainPriceRenderer.Series;
    this.mainWindow.IndicatorStorageRenderer.Indicators.push(indicatorRenderer);

    //
    this.CorrectIndicatorWindows();


};

TerceraChart.prototype.ReplaceIndicators = function (indicators)
{
    var i;
    var len = indicators.length;
    for (i = 0; i < len; i++)
    {
        let indRenderer = indicators[i];
        let ind = indRenderer.Indicator();
        if (indRenderer.Series && indRenderer.Series._cashItem)
            indRenderer.Series._cashItem.RemoveIndicator(ind)

        indRenderer.Series = this.mainPriceRenderer.Series;
        ind = indRenderer.Series._cashItem.AddIndicator(ind);
        if (ind !== null)
            ind.Refresh();

        ind.MainChart = this;
        ind.TimeFrameInfo = this.TimeFrameInfo();
        ind.Symbol = this.Instrument().GetInteriorID();

        indRenderer.InvalidState = false;
    }
};

/// <summary>
///
/// </summary>
TerceraChart.prototype.RemoveIndicator = function (indicatorRenderer, callCorrectIndicatorWindows = true)
{
    var dispose = true;
    if (indicatorRenderer != null)
    {
        var ind = indicatorRenderer.Indicator();
        //var ptlInd = ind as PTLIndicator;
        //if (ptlInd != null)
        //    ptlInd.UninitializeReason = UninitializeReasonType.Remove;

        //remove from cashItem
        this.mainPriceRenderer.Series._cashItem.RemoveIndicator(ind);

        if (indicatorRenderer.WindowsNumber < this.windowsContainer.Windows.length)
        {
            var remIndex = this.windowsContainer.Windows[indicatorRenderer.WindowsNumber].IndicatorStorageRenderer.Indicators.indexOf(indicatorRenderer);
            this.windowsContainer.Windows[indicatorRenderer.WindowsNumber].IndicatorStorageRenderer.Indicators.splice(remIndex, 1);
        }
        indicatorRenderer.Dispose();

        if (callCorrectIndicatorWindows)
            this.CorrectIndicatorWindows();

        //Hashtable h = new Hashtable();
        //h["PTLIndicator"] = ind;
        //h["dispose"] = dispose;
        //SeparateThread.QueueUserWorkItem(new System.Threading.WaitCallback(RemovingIndicatorAsync), h);
        ind.Dispose()
    }
}

/// <summary>
///
/// </summary>
TerceraChart.prototype.RemoveAllIndicators = function ()
{
    var allIndicatorRenderers = this.IndicatorRenderers();

    for (var i = 0; i < allIndicatorRenderers.length; i++)
        this.RemoveIndicator(allIndicatorRenderers[i], true);

    this.CorrectIndicatorWindows();
    this.IsDirty();
}

TerceraChart.prototype.IndicatorRenderers = function ()
{
    if (this.windowsContainer == null)
        return null;

    var res = [];

    for (var i = 0; i < this.windowsContainer.Windows.length; i++)
    {
        var j;
        var inds = this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators;
        var l_ = inds.length;
        for (j = 0; j < l_; j++)
            res.push(inds[j]);
    }
    return res;
};

TerceraChart.prototype.FindIndicatorRendererByPtlIndicator = function (processor)
{
    for (var i = 0; i < this.windowsContainer.Windows.length; i++)
    {
        var indicatorsStorage = this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators;
        for (var j = 0; j < indicatorsStorage.length; j++)
        {
            if (indicatorsStorage[j].Indicator() == processor)
                return indicatorsStorage[j];
        }
    }
    return null;
}

/// <summary>
/// Перемещает индикаторы на соответсвующие окна
/// </summary>
TerceraChart.prototype.CorrectIndicatorWindows = function ()
{
    var winCount = this.windowsContainer.Windows.length;

    var added = [];
    var removed = [];
    for (var i = 0; i < winCount; i++)
    {
        for (var j = 0; j < this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators.length; j++)
        {
            // Нашли индюк не на своем месте
            var indRenderer = this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators[j];
            if (indRenderer != null && indRenderer.WindowsNumber != i)
            {
                var remIndex = this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators.indexOf(indRenderer);
                this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators.splice(remIndex, 1);

                // Есть окно с подходящим номером
                if (indRenderer.WindowsNumber < this.windowsContainer.Windows.length)
                    this.windowsContainer.Windows[indRenderer.WindowsNumber].IndicatorStorageRenderer.Indicators.push(indRenderer);
                // Нужно создавать новое окно
                else
                {
                    // Создаем нужное количество окон
                    while (indRenderer.WindowsNumber >= this.windowsContainer.Windows.length)
                        this.AddWindow();

                    this.windowsContainer.Windows[indRenderer.WindowsNumber].IndicatorStorageRenderer.Indicators.push(indRenderer);
                }
            }
        }
    }
    // Удаляем пустые окна
    winCount = this.windowsContainer.Windows.length;
    var windowsToRemove = [];
    for (var i = 1; i < winCount; i++)
    {
        var indRenderer = this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators[0];

        if (indRenderer == null || indRenderer.Indicator == null)
            windowsToRemove.push(this.windowsContainer.Windows[i]);
    }
    for (var i = 0; i < windowsToRemove.length; i++)
        this.RemoveWindow(windowsToRemove[i]);

    //
    // Корректировка индексов (http://tp.pfsoft.net/entity/38104)
    //
    for (var i = 1; i < this.windowsContainer.Windows.length; i++)
    {
        for (var j = 0; j < this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators.length; j++)
        {
            var rend = this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators[j];
            rend.WindowsNumber = i;
        }
    }

    //
    this.CorrectPlateRenderers();
    this.LayoutWindowsContainers();
    this.IsDirty();
};

TerceraChart.prototype.SaveOverlays = function ()
{
    var overlayArr = [];

    var overlays = this.Overlays;
    var len = overlays ? overlays.length : 0;
    for (var i = 0; i < len; i++)
    {
        var overlay = overlays[i];
        overlayArr.push({
            attributes: {
                instrument: overlay.ActiveInstrument
                    ? overlay.ActiveInstrument.GetInteriorID()
                    : overlay.Instrument,
                historyType: overlay.HistoryType,
                style: overlay.ChartDrawingTypeGet(),
                showOnChart: overlay.mainPriceRenderer.Visible ? 1 : 0,
                showMarker: overlay.ShowMarker ? 1 : 0,
                separateScale: overlay.SeparateScaleGet() ? 1 : 0
            },
            mainPriceRendererProperties: DynProperty.serialize(overlay.mainPriceRenderer.Properties())
        });
    }
    return { overlays: overlayArr };
};

TerceraChart.prototype.LoadOverlays = function (xml)
{
    if (!xml) return;

    try
    {
        this.RemoveAllOverlays();

        var overlaysJsonArr = xml.overlays;
        if (Array.isArray(overlaysJsonArr))
        {
            var len = overlaysJsonArr ? overlaysJsonArr.length : 0;
            for (var i = 0; i < len; i++)
                this.LoadOverlay(overlaysJsonArr[i]);
        }
        else this.LoadOverlay(overlaysJsonArr);
    }
    catch (ex) 
    {
        ErrorInformationStorage.GetException(ex);
        console.log(ex);
    }
};

TerceraChart.prototype.LoadOverlay = function (jsonItem)
{
    let attributes = jsonItem.attributes

    let overlay = this.AddOverlayInstrument(
        attributes.instrument,
        false,
        parseInt(attributes.style),
        parseInt(attributes.historyType))

    if (!overlay) return

    overlay.mainPriceRenderer.Visible = attributes.showOnChart == '1'
    overlay.ShowMarker = attributes.showMarker == '1'
    overlay.SeparateScaleSet(attributes.separateScale == '1')

    let properties = DynProperty.deserialize(jsonItem.mainPriceRendererProperties)
    overlay.mainPriceRenderer.callBack(properties)
}

TerceraChart.prototype.SaveIndicators = function ()
{
    var currentSymbol = (this.model != null && this.model.instrument != null) ? this.model.instrument.GetInteriorID() : "";
    var index = 0;

    var indicators = {};
    indicators.indicator = [];

    var allIndicators = this.IndicatorRenderers();
    for (var i = 0; i < allIndicators.length; i++)
    {
        var indicator = allIndicators[i];
        if (indicator == null)
        {
            index++;
            continue;
        }

        var p = indicator.PTLIndicator;

        // let myIns = DataCache.getInstrumentByName(p.Symbol);
        // let mySymbol = myIns.GetInteriorID();
        // if (mySymbol != currentSymbol)
        //     continue;
        if (currentSymbol.indexOf(p.Symbol) === -1)
            continue

        var indicatorDataToSave = {};
        if (p.Script)
            indicatorDataToSave.ProjectName = p.Script.ProjectName;

        var props = indicator.Properties();

        var dp = DynProperty.getPropertyByName(props, "Win num");
        if (dp != null)
            dp.value = dp.value - index;

        indicatorDataToSave.properties = DynProperty.serialize(props);
        indicatorDataToSave.LinesCount = p.LinesCount;
        indicatorDataToSave.SeparateWindow = p.SeparateWindow;
        indicators.indicator.push(indicatorDataToSave);
    }
    return indicators;
}

TerceraChart.prototype.LoadIndicators = function (xml)
{
    if (!xml)
        return;

    var indicatorArray = xml.indicator;
    var i;
    var len = indicatorArray.length;
    if (!Array.isArray(indicatorArray))
    {
        try
        {
            this.LoadIndicator(indicatorArray);
        }
        catch (ex) 
        {
            ErrorInformationStorage.GetException(ex);
        }
    }
    else
    {
        try
        {
            for (i = 0; i < len; i++)
                this.LoadIndicator(indicatorArray[i]);
        }
        catch (ex) 
        {
            ErrorInformationStorage.GetException(ex);
        }
    }
}

TerceraChart.prototype.LoadIndicator = function (xml)
{
    var indicator = IndicatorManager.GetIndicator(xml.ProjectName);

    var indicatorProperties = DynProperty.deserialize(xml.properties);

    var indicatorRenderer = this.GetCharIndicatorRenderer(indicator, this);
    indicatorRenderer.callBack(indicatorProperties);

    ////добавляю на чарт
    this.mainWindow.IndicatorStorageRenderer.Indicators.push(indicatorRenderer);
    this.CorrectIndicatorWindows();

    if (!indicator.MainChart) //#50999
        indicator.MainChart = this;
}

TerceraChart.prototype.AddWindow = function ()
{
    var priceScaleRender = this.GetChartPriceScaleRenderer(this, this.priceScaleRendererSettings, TerceraChartPriceScaleRendererWindowType.Indicator);
    var leftPriceScaleRender = this.GetChartPriceScaleRenderer(this, this.priceScaleRendererSettings, TerceraChartPriceScaleRendererWindowType.Indicator);
    leftPriceScaleRender.Aligment = TerceraChartPriceScaleRendererAligment.Left;

    var toolRenderer = new TerceraChartToolInstanceRenderer(this.ChartID, this);
    toolRenderer.Instrument = this.Instrument();
    var ww = this.GetChartWindow(priceScaleRender, toolRenderer, leftPriceScaleRender);
    ww.PointsConverter = new CashItemPointsCoverter(ww, this.mainPriceRenderer.Series);
    ww.WindowsContainer = this.windowsContainer;

    ww.Renderers.splice(1, 0, this.mainWindow.GetMainThreadRenderer("TerceraChartNewToolRenderer"));
    ww.IndicatorStorageRenderer = new TerceraChartIndicatorStorageRenderer(this)
    ww.Renderers.push(ww.IndicatorStorageRenderer);

    this.CalculateNewWindowHeightKoef(ww);
    this.windowsContainer.Windows.push(ww);
    priceScaleRender.window = ww;
    leftPriceScaleRender.window = ww;

    ww.ToolsRenderer.SelectedToolsListChanged.Subscribe(this.ToolsRenderer_SelectedToolsListChanged, this);

    return ww;
};

TerceraChart.prototype.CalculateNewWindowHeightKoef = function (ww)
{
    //normalize HeightKoef
    var normalizeKoef = 100 / this.windowsContainer.mainWindow.HeightKoef;
    for (var i = 0; i < this.windowsContainer.Windows.length; i++)
    {
        this.windowsContainer.Windows[i].HeightKoef *= normalizeKoef;
    }
    //add first sub window
    if (this.windowsContainer.Windows.length == 1)
        ww.HeightKoef = this.windowsContainer.mainWindow.HeightKoef / 2;
    //add second or third window
    if (this.windowsContainer.Windows.length >= 2)
    {
        var koefSum = 0;
        for (var i = 0; i < this.windowsContainer.Windows.length; i++)
        {
            if (!this.windowsContainer.Windows[i].IsMainWindow)
                koefSum += this.windowsContainer.Windows[i].HeightKoef;
        }
        ww.HeightKoef = koefSum / (this.windowsContainer.Windows.length - 1);
        koefSum += ww.HeightKoef;

        //max height for sun windows
        var maxSubWindowHeight = 0;

        if (this.windowsContainer.Windows.length == 2)
            maxSubWindowHeight = this.windowsContainer.mainWindow.HeightKoef;
        if (this.windowsContainer.Windows.length > 2)
            maxSubWindowHeight = 2 * this.windowsContainer.mainWindow.HeightKoef;

        //recalculate all HeightKoef
        if (koefSum > maxSubWindowHeight)
        {
            for (var i = 0; i < this.windowsContainer.Windows.length; i++)
            {
                if (!this.windowsContainer.Windows[i].IsMainWindow)
                    this.windowsContainer.Windows[i].HeightKoef *= maxSubWindowHeight / koefSum;
            }

            ww.HeightKoef *= maxSubWindowHeight / koefSum;
        }
    }
}

TerceraChart.prototype.RemoveWindow = function (ww)
{
    ww.ToolsRenderer.SelectedToolsListChanged.UnSubscribe(this.ToolsRenderer_SelectedToolsListChanged, this);

    var remIndex = this.windowsContainer.Windows.indexOf(ww);
    this.windowsContainer.Windows.splice(remIndex, 1);
    ww.Dispose();
}

TerceraChart.prototype.ToolsRenderer_SelectedToolsListChanged = function ()
{
    this.TerceraChartActionProcessor.StateChanged.Raise();
}

/// <summary>
/// определяем для всех вложенных окон нужно ли место под рисование плашек индюков/оверлеев
/// </summary>
TerceraChart.prototype.CorrectPlateRenderers = function ()
{

    //
    // определяем для всех вложенных окон нужно ли место под рисование плашек индюков/оверлеев
    //
    var winCount = this.windowsContainer.Windows.length;
    for (var i = 0; i < winCount; i++)
    {
        var plateRenderer = this.windowsContainer.Windows[i].GetMainThreadRenderer("TerceraChartPlateRenderer");

        if (i == 0)
        {
            var listOv = [];
            if (this.Overlays.length > 0)
            {
                if (plateRenderer == null)
                {
                    plateRenderer = new TerceraChartPlateRenderer(this);
                    this.windowsContainer.Windows[i].Renderers.push(plateRenderer);
                }
                for (var j = 0; j < this.Overlays.length; j++)
                {
                    var overl = plateRenderer.GetPlateOverlay(this.Overlays[j], true);
                    listOv.push(overl);
                }
            }
            if (plateRenderer != null)
                plateRenderer.UpdatePlateOverlays(listOv);
        }


        var listInd = [];
        for (var j = 0; j < this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators.length; j++)
        {
            try
            {
                if (plateRenderer == null)
                {
                    plateRenderer = this.GetCharPlateIndicatorRenderer(this);
                    this.windowsContainer.Windows[i].Renderers.push(plateRenderer);
                }
                if (this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators[j].Indicator() != null)
                {
                    var indic = plateRenderer.GetPlateIndicator(this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators[j], true);
                    listInd.push(indic);
                }
            }
            catch (ex) 
            {
                ErrorInformationStorage.GetException(ex);
            }
        }
        if (plateRenderer != null)
        {
            plateRenderer.UpdatePlateIndicators(listInd);
            plateRenderer.WinNum = i;
        }

        if ((plateRenderer != null && !plateRenderer.IsEmpty()) || this.windowsContainer.Windows[i].TerceraChartOverlayWindow)
        {
            this.windowsContainer.Windows[i].HeaderVisible = true;
            this.windowsContainer.Windows[i].PaddingTop = this.windowsContainer.Windows[i].HeaderHeight + 1;
        }
        else
        {
            this.windowsContainer.Windows[i].PaddingTop = this.HeaderHeight + 1;
            this.windowsContainer.Windows[i].HeaderVisible = false;
        }
    }
    //TODO MB SC2 Optimization ++13th
    this.IsDirty(LayersEnum.CrossHair);
    this.Draw()
}

TerceraChart.prototype.getAllIndicatorRenderers = function ()
{
    const res = [];
    const winCount = this.windowsContainer.Windows.length;
    for (let i = 0; i < winCount; i++)
    {
        let indicatorsStorage = this.windowsContainer.Windows[i].IndicatorStorageRenderer.Indicators;
        for (const element of indicatorsStorage)
        {
            res.push(element);
        }
    }
    return res;
}

/// <summary>
/// Конструктор тулзовин
/// </summary>
TerceraChart.prototype.CreateTool = function (toolType)
{
    var tool = DataCacheToolFactory.Create(toolType, this.FDataCache);
    tool.AssignedSymbol = this.Instrument().GetInteriorID();
    tool.Instrument = this.Instrument();
    return tool;
}


TerceraChart.prototype.ProcessChangeTool = function (tool)
{
    if (tool != null && this.Instrument() != null && tool.AssignedSymbol == this.Instrument().GetInteriorID())
        this.IsDirty(LayersEnum.Tools);
}


TerceraChart.prototype.AddOverlayInstrument = function (instrumentName, callUpdateOverlays, style, historyType)
{
    var overlay = new TerceraChartOverlay(this);
    overlay.Instrument = instrumentName;
    overlay.HistoryType = historyType;
    overlay.ChartDrawingTypeSet(style);
    this.overlayStorageRenderer.OverlaysWindows.push(overlay.window);
    this.Overlays.push(overlay);
    this.CorrectPlateRenderers();

    // Need for update OverlayWindow rectangle
    this.LayoutWindowsContainers();

    // Обновляем оверлеи
    if (callUpdateOverlays)
        this.UpdateOverlay();

    this.IsDirty();
    return overlay;
}

//#region ICrosshairable

TerceraChart.prototype.TimeScaleRect = function ()
{
    return this.windowsContainer.timeScaleRenderer.Rectangle;
};

TerceraChart.prototype.TrackCursorInvert = function ()
{
    // TODO. Replace with mainThreadDrawingRenderers.
    var renderers = this.mainWindow.Renderers;
    var len = renderers.length;
    for (var i = 0; i < len; i++)
    {
        var renderer = renderers[i];
        if (renderer instanceof TerceraChartCrossHairRenderer)
        {
            renderer.ShowTrackVisibleLine = !renderer.ShowTrackVisibleLine;
            //renderer.ShowTrackTimeLine = !renderer.ShowTrackTimeLine;
        }
    }
};

//#endregion

//#region Tools

// TODO. Optimize?
Object.defineProperty(TerceraChart.prototype, 'AllWindowsHoverAndSelectedTools', {
    get: function ()
    {
        var allTools = [];

        var wc = this.windowsContainer;
        if (!wc) return allTools;

        var windows = wc.Windows;
        if (windows)
        {
            var len = windows.length;
            for (var i = 0; i < len; i++)
            {
                var toolsRenderer = windows[i].ToolsRenderer;
                if (toolsRenderer)
                    allTools = allTools.concat(toolsRenderer.GetHoverAndSelectedToolsList());
            }
        }

        return allTools;
    }
});

//#endregion


TerceraChart.prototype.repopulate = function ()
{
    var allIndicators = this.IndicatorRenderers();

    var i;
    var len = allIndicators.length;
    for (i = 0; i < len; i++)
    {
        var curInd = allIndicators[i].Indicator();
        curInd.repopulate();
    }

    this.RefreshChart();
};

TerceraChart.prototype.newQuote = function (message)
{
    let mainPriceRenderer = this.mainPriceRenderer
    let ins = this.Instrument()

    if (mainPriceRenderer && ins)
    {
        /* TODO. 
        mainPriceRenderer.DayHigh = ins.OHLCCalculator.HighSpread(Account, TerceraChartHistoryType.GetOriginalHistoryType(ChartHistoryType, ins))
        mainPriceRenderer.DayLow = ins.OHLCCalculator.LowSpread(Account, TerceraChartHistoryType.GetOriginalHistoryType(ChartHistoryType, ins))
        */
    }

    this.IsDirty()
}

/// <summary>
/// Удалить оверлей
/// </summary>
TerceraChart.prototype.RemoveOverlay = function (over)
{
    if (this.ActiveOverlayGet() === over)
        this.ActiveOverlaySet(null);

    ArrayUtils.RemoveElementFromArray(this.overlayStorageRenderer.OverlaysWindows, over.window)
    ArrayUtils.RemoveElementFromArray(this.Overlays, over)
    this.CorrectPlateRenderers();
    over.Dispose();
    this.IsDirty();
}

/// <summary>
/// Удалить оверлей
/// </summary>
TerceraChart.prototype.RemoveAllOverlays = function ()
{
    var overlays = this.Overlays;
    var i = overlays.length;
    while (i--) this.RemoveOverlay(overlays[i]);
}

TerceraChart.prototype.windowsContainer_AfterResizeWindows = function ()
{
    if (this.overlayStorageRenderer != null)
        this.overlayStorageRenderer.LayoutWindows();
}

/// <summary>
/// обновить размеры контейнеров окон, окон, и их рендеререов, нужно вызывать на ресайзы, (добавление/удаление индюков, оверлеев, смену инструмента)
/// для корректного перерасположения рендереров
/// </summary>
/// <summary>
/// Определят расположение контейнеров
/// </summary>
TerceraChart.prototype.LayoutWindowsContainers = function ()
{
    if (this.windowsContainer != null)
    {
        this.windowsContainer.Rectangle.Width = this.w;
        this.windowsContainer.Rectangle.Height = this.h;
    }

    //for (var i = 0; i < this.windowsContainer.length; i++)
    this.windowsContainer.LayoutWindows();

    this.IsDirty();
}

TerceraChart.prototype.ThemeChanged = function (applyToTable)
{

    /*foreach (var renderer in mainThreadDrawingRenderers)
    renderer.ThemeChanged(applyToTable);*/

    this.TerceraChartFont = ThemeManager.Fonts.Font_7F_regular.copy();
    //this.dragDropImage = App.ThemeManager.CurrentTheme.dragOverImg;

    //if (Utils.ResetLayouts)
    //{
    this.BottomColor = ThemeManager.CurrentTheme.Chart_BottomColor;
    this.TopColor = ThemeManager.CurrentTheme.Chart_TopColor;
    //}

    this.priceScaleRendererSettings.ThemeChanged(applyToTable);
    this.timeScaleRendererSettings.ThemeChanged(applyToTable);

    var windowsContainer = this.windowsContainer;
    if (windowsContainer)
    {
        windowsContainer.ThemeChanged();

        var wcRenderers = windowsContainer.Renderers;
        for (var i = 0; i < wcRenderers.length; i++)
        {
            var wcRenderer = wcRenderers[i];
            if (wcRenderer.ThemeChanged)
                wcRenderer.ThemeChanged(applyToTable);
        }

        for (var i = 0; i < windowsContainer.Windows.length; i++)
        {
            var allRenderers = windowsContainer.Windows[i].GetAllRenderers();
            for (var j = 0; j < allRenderers.length; j++)
            {
                var renderer = allRenderers[j];
                if (renderer.ThemeChanged)
                    renderer.ThemeChanged(applyToTable);
            }
        }
    }

    this.IsDirty();
};

TerceraChart.prototype.Localize = function ()
{
    var wc = this.windowsContainer;
    if (!wc) return;

    var windowsContainer = this.windowsContainer;
    if (windowsContainer) windowsContainer.Localize();

    this.IsDirty();
};

TerceraChart.DEFAULT_BARSTORIGHT_PERCENT = 10;

/// <summary>
/// Current order plasing type is OCO
/// </summary>
TerceraChart.prototype.getIsOCOMode = function ()
{
    return this.TerceraChartTradingToolsRenderer.IsOCOMode;
};
TerceraChart.prototype.setIsOCOMode = function (value)
{
    this.TerceraChartTradingToolsRenderer.IsOCOMode = value;
};

TerceraChart.prototype.Dispose = function ()
{
    this.loadHistoryAbortController?.abort();
    this.loadOverlayHistoryAbortController?.abort();

    this.unsubscribeTrades()

    if (this.mainPriceRenderer.Series)
        this.mainPriceRenderer.Series.Dispose();
    this.chartController.Dispose();

    this.autoLoadManager.dispose();
}


TerceraChart.prototype.getSnapToCandle = function ()
{
    return this.snapToCandle;
}
TerceraChart.prototype.setSnapToCandle = function (value)
{
    if (this.snapToCandle === value)
        return;

    this.snapToCandle = value;

    var i = 0;
    var len = this.mainWindow.Renderers.length;
    for (i; i < len; i++)
    {
        if (this.mainWindow.Renderers[i].ApplyNewSnapMode)
            this.mainWindow.Renderers[i].ApplyNewSnapMode(this.snapToCandle)
    }
}


TerceraChart.prototype.GetHistoryParams = function ()
{
    let Instrument = this.model.GetInstrument();
    let Account = DataCache.MainAccountNew || this.model.GetAccount();
    let historyParams = new ReloadHistoryParams();


    historyParams.TimeFrameInfo = this.TimeFrameInfo();
    historyParams.userPin = Account.userPin;
    historyParams.userId = Account.userID;

    /// <summary>
    /// если выбран тип истории default тут true, иначе НЕЛЬЗЯ менять тип, т.к. юзер задал свой любимый. юзать historyType
    /// </summary>
    historyParams.UseDefaultInstrumentHistoryType = this.ChartHistoryType() == ChartHistoryType.Default;
    historyParams.symbol = Instrument.ShortName;
    historyParams.route = Instrument ? Instrument.Route : null;

    historyParams.FromTime = this.model.HistoryFrom;// Utils.DateTimeToUTC(this.model.HistoryFrom);
    historyParams.ToTime = this.model.GetHistoryTo();//Utils.DateTimeToUTC(this.model.GetHistoryTo());

    return historyParams;
}

TerceraChart.prototype.GetTooltip = function (e)
{
    var i
    var len = this.mainWindow.Renderers.length;
    for (i = 0; i < len; i++)
    {
        var renderer = this.mainWindow.Renderers[i]
        var newTooltip = renderer.GetTooltip(e);
        if (newTooltip != null)
        {
            return newTooltip;
        }
    }

    var newTooltip = this.windowsContainer.GetTooltip(e);
    if (newTooltip != null)
    {
        return newTooltip;
    }

    return null;
}

TerceraChart.prototype.UpdateTooltip = function (e)
{
    if (!e)
        return;

    var newTooltip = null;
    try
    {
        newTooltip = this.GetTooltip(e);
    }
    finally
    {
        this.OnUpdateTooltip.Raise(newTooltip);
    }
}

TerceraChart.prototype.GetCursor = function (e)
{
    if (this.MouseState == TerceraChartMouseState.Moving && this.HasCorrectData())
    {
        var activeWindow = this.ActiveOverlayGet() != null ? this.ActiveOverlayGet().window : this.HoverWindow;
        if (activeWindow != null)
        {
            return activeWindow.AutoScale ? Cursors.SizeWE : Cursors.SizeAll;
        }
    }

    var i
    var len = this.mainWindow.Renderers.length;
    for (i = 0; i < len; i++)
    {
        var renderer = this.mainWindow.Renderers[i]
        var newCursor = renderer.GetCursor(e);
        if (newCursor != null)
        {
            return newCursor;
        }
    }

    var newCursor = this.windowsContainer.GetCursor(e);
    if (newCursor != null)
    {
        return newCursor;
    }

    return null;
}

/// <summary>
/// Update Cursor, e == null, when keyDown, KeyUp, MouseLeave..
/// </summary>
/// <param name="e"></param>
TerceraChart.prototype.UpdateCursor = function (e = null)
{
    //todo
    if (e == null) // e == null, when keyDown, KeyUp, MouseLeave..
        //e = this.ImproveArgs(new MouseEventArgs(System.Windows.Forms.MouseButtons.None, 0, CursorPosition.X, CursorPosition.Y, 0));
        return;

    var newCursor = null;
    try
    {
        newCursor = this.GetCursor(e);
    }
    finally
    {
        // поменялся или стал обратно дефаулт
        if (this.Cursor !== newCursor && newCursor !== null || (this.Cursor !== Cursors.Default && newCursor === null))
        {
            this.IsDirty(LayersEnum.CrossHair);
            this.Cursor = newCursor;
            this.terceraChartPanelContext.applyCursor(newCursor);
        }
    }
}

TerceraChart.prototype.ForceUpdateCursor = function ()
{
    if (!this.lastMouseMoveArgs)
        return;

    var newCursor = this.GetCursor(this.lastMouseMoveArgs);
    if (newCursor)
    {
        this.IsDirty(LayersEnum.CrossHair);
        this.Cursor = newCursor;
        this.terceraChartPanelContext.applyCursor(newCursor);
    }
};

Object.defineProperty(TerceraChart.prototype, 'DataExtenderAmount',
    {
        get: function ()
        {
            let series = this.MainCashItemSeries()
            if (!series)
                return 0

            let list = series.FIndexList
            return list.length > 0 ? list[0] : 0
        }
    })

let TerceraChartMouseState = { None: 0, Moving: 1 };
/// <summary>
///
/// </summary>
export let TerceraChartMouseEventArgs = function (ww, ev)
{
    /// <summary>
    ///
    /// </summary>
    //public TerceraChartMouseEventArgs()
    //: base(ev.Button, ev.Clicks, ev.X, ev.Y, ev.Delta)
    //{
    this.window = ww;
    this.NeedRedraw = false;
    this._baseEvent = ev;
    this.Button = ev.button;
    this.X = ev.offsetX;
    this.Y = ev.offsetY;
    this.Delta = -1 * parseInt(ev.deltaY);
    this.Location = new Point(this.X, this.Y);
    this.ClientLocation = new Point(ev.clientX, ev.clientY);
    //}
}

let TerceraChartPriceScaleLayoutInfo = function ()
{
    this.PreferredWidthScales = 0;
    this.PreferredWidthScalesLeft = 0;

    /// <summary>
    /// Эти условия подразумевают добававление правой шкалы
    /// </summary>
    this.HasOverlays = false;
    this.HasDoubledIndicator = false;
}


let LayersEnum =
{
    Main: 0,
    Quotes: 1,
    CrossHair: 2,
    Tools: 3
}


let Layer = function (newCanvas)
{
    this.canvas = newCanvas;
    this.context = newCanvas.getContext('2d');
    this.isDirty = true;
};

//#region Portfolio Returns

TerceraChart.prototype.setPortfolioReturnsRendererProperty = function (key, value)
{
    let portfolioRenderer = this.PortfolioChartReturnsRenderer

    if (!portfolioRenderer) return

    portfolioRenderer[key] = value
}

//#endregion

TerceraChart.prototype.setDataForTradingCentralRenderer = function (data)
{
    let tradingCentralRenderer = this.TradingCentralRenderer
    if (!tradingCentralRenderer) return

    tradingCentralRenderer.setLinesValues(data)
}

TerceraChart.prototype.setAutoLoadHistory = function (value)
{
    this.autoLoadManager.allowAutoLoad = value;
}

TerceraChart.prototype.resetCurrentView = function ()
{
    this.autoLoadManager.resetCurrentView();
}